import { useState, useEffect, useCallback, useMemo } from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import InputAdornment from "@mui/material/InputAdornment";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import OutlinedInput from "@mui/material/OutlinedInput";
import CircularProgress from "@mui/material/CircularProgress";
import Checkbox from "@mui/material/Checkbox";
import Switch from "@mui/material/Switch";
import type { SelectChangeEvent } from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import type { DrawnZone } from "./ZonesTab";
import { fetchAndApplyRecycleBinTotals } from "../../utils/recycleBinTotalsSync.ts";
import { userTimezoneHeaders } from "../../utils/userTimezoneHeader";
import React from "react";
import toast from "react-hot-toast";

declare const process: { env: Record<string, string | undefined> };
const API_BASE = process.env.REACT_APP_API_URL_FOR_AI_DETECTION || "";

/** Backend / network errors in this tab (overrides app-level Toaster position for these toasts). */
const TOAST_ERROR_POSITION = { position: "bottom-center" as const };

/** Extract use-case array from GET /use-cases/ response */
function extractUseCasesFromResponse(
  body: unknown,
): Array<{ id: string; name?: string }> {
  if (Array.isArray(body)) return body;
  if (body && typeof body === "object") {
    const obj = body as Record<string, unknown>;
    const arr = (obj.data ?? obj.results ?? obj.Data) as unknown;
    if (Array.isArray(arr)) return arr;
  }
  return [];
}

/** Request body for POST /cameras (Create Camera — sync before camera-ai-config save). */
export interface PostCamerasCreateBody {
  name: string;
  stream_url: string;
}

/**
 * POST /cameras before saving camera AI config (Zone Configuration flow).
 * Accepts 201 Created; surfaces validation errors from 422.
 */
async function postCamerasCreate(
  base: string,
  body: PostCamerasCreateBody,
): Promise<void> {
  const res = await fetch(`${base}/cameras`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...userTimezoneHeaders(),
    },
    body: JSON.stringify(body),
  });
  if (res.ok) return;
  const text = await res.text();
  let message = `Failed to create camera (${res.status})`;
  try {
    const json = JSON.parse(text) as {
      detail?: string | Array<{ msg?: string }>;
      message?: string;
    };
    if (json.detail && Array.isArray(json.detail)) {
      message =
        json.detail
          .map((d: { msg?: string }) => d.msg)
          .filter(Boolean)
          .join("; ") || message;
    } else if (typeof json.detail === "string") {
      message = json.detail;
    } else if (typeof json.message === "string" && json.message.trim()) {
      message = json.message;
    }
  } catch {
    if (text) message = text.slice(0, 200);
  }
  throw new Error(message);
}

/**
 * Request body for POST /camera-ai-configs/ — create one config (camera + zone + use case).
 */
export interface PostCameraAiConfigBody {
  camera_id: string;
  use_case_id: string;
  zone_id: string;
  zone_profile_id: string | null;
  severity: string;
  detection_cooldown_interval: number;
  is_active: boolean;
}

/**
 * Request body for PUT /camera-ai-configs/{config_id}.
 * Backend requires `config_id` in the JSON body as well as in the URL path.
 */
export interface PutCameraAiConfigBody {
  config_id: string;
  camera_id: string;
  use_case_id: string;
  zone_id: string;
  /** Send `null` when unset so the key is always present for strict validators. */
  zone_profile_id: string | null;
  severity: string;
  is_active: boolean;
  detection_cooldown_interval?: number;
}

/** POST /camera-ai-configs/ — single flat body (create config by zone). */
async function postCameraAiConfigsCreate(
  base: string,
  body: PostCameraAiConfigBody,
): Promise<void> {
  const root = base.replace(/\/$/, "");
  const res = await fetch(`${root}/camera-ai-configs/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...userTimezoneHeaders(),
    },
    body: JSON.stringify(body),
  });
  if (res.ok) return;
  const text = await res.text();
  let message =
    res.status === 409
      ? "An active configuration already exists for this camera, zone, and use case."
      : `Failed to create config (${res.status})`;
  try {
    const json = JSON.parse(text) as {
      detail?: string | Array<{ msg?: string }>;
      message?: string;
    };
    if (json.detail && Array.isArray(json.detail)) {
      message =
        json.detail
          .map((d: { msg?: string }) => d.msg)
          .filter(Boolean)
          .join("; ") || message;
    } else if (typeof json.detail === "string") {
      message = json.detail;
    } else if (typeof json.message === "string" && json.message.trim()) {
      message = json.message;
    }
  } catch {
    if (text) message = text.slice(0, 200);
  }
  throw new Error(message);
}

/** Request body for PATCH /camera-ai-configs/toggle */
export interface PatchCameraAiConfigToggleBody {
  id: string;
  is_active: boolean;
}

/** Sortable columns for AI Models table Name header (Zones tab pattern). */
type AiModelOrderBy = "name";

export interface ZoneAIModel {
  id: string;
  zoneName: string;
  aiModel: string;
  confidenceScore: number;
  objectTypes: string[];
  createdAt: Date;
  /** API config id for PUT /camera-ai-configs/{config_id} */
  configId?: string;
  zoneId?: string;
  useCaseId?: string;
  zoneProfileId?: string | null;
  isActive?: boolean;
  /** From API use_cases[].profile_count; shown in Object Type column when present */
  profileCount?: number;
}

/** Loose UUID v4-style check (matches backend ids from Postgres etc.). */
function isUuidString(value: string | undefined | null): boolean {
  return (
    typeof value === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      value.trim(),
    )
  );
}

/**
 * Primary key for PUT /camera-ai-configs/{config_id}. Must be the camera_ai_configs row id.
 * If several UUID fields exist, prefer one that is not `zone_id` (zone id in the path → "not found").
 */
function resolveCameraAiConfigRowId(
  config: CameraAiConfigItem & Record<string, unknown>,
): string | undefined {
  const c = config as Record<string, unknown>;
  const zid = typeof c.zone_id === "string" ? c.zone_id.trim() : "";
  const keys = [
    "camera_ai_config_id",
    "config_id",
    "cameraAiConfigId",
    "camera_ai_config_uuid",
    "id",
    "uuid",
    "_id",
  ] as const;
  const found: string[] = [];
  for (const k of keys) {
    const raw = c[k];
    if (typeof raw === "string" && isUuidString(raw)) found.push(raw.trim());
  }
  const distinct = [...new Set(found)];
  const notZone = distinct.filter((u) => !zid || u !== zid);
  if (notZone.length > 0) return notZone[0];
  if (distinct.length > 0) return distinct[0];
  return undefined;
}

/**
 * Unwrap nested API shapes and merge so `id` / aliases are visible for PUT.
 * Handles `{ data: {...} }`, `{ camera_ai_config: {...} }`.
 */
export function normalizeCameraAiConfigItem(
  raw: unknown,
): CameraAiConfigItem | null {
  if (!raw || typeof raw !== "object") return null;
  let o: Record<string, unknown> = { ...(raw as Record<string, unknown>) };

  const mergeChild = (key: string) => {
    const v = o[key];
    if (v && typeof v === "object" && !Array.isArray(v)) {
      o = { ...o, ...(v as Record<string, unknown>) };
    }
  };
  mergeChild("data");
  mergeChild("camera_ai_config");

  const zone_id = o.zone_id != null ? String(o.zone_id).trim() : "";
  const camera_id = o.camera_id != null ? String(o.camera_id).trim() : "";
  if (!zone_id || !camera_id) return null;

  let use_cases = (
    Array.isArray(o.use_cases) ? o.use_cases : []
  ) as CameraAiConfigItem["use_cases"];

  /** Flat row: use_case_* and profile_count on the parent when `use_cases` is empty. */
  if (use_cases.length === 0) {
    const flatUcId =
      o.use_case_id != null ? String(o.use_case_id).trim() : "";
    if (flatUcId) {
      const pcRaw = o.profile_count;
      const profileCount =
        typeof pcRaw === "number" && Number.isFinite(pcRaw)
          ? pcRaw
          : typeof pcRaw === "string" && pcRaw.trim() !== ""
            ? Number.parseInt(pcRaw, 10)
            : undefined;
      const ucName =
        typeof o.use_case_name === "string" ? o.use_case_name.trim() : undefined;
      const zp =
        o.zone_profile_id != null && o.zone_profile_id !== ""
          ? String(o.zone_profile_id).trim()
          : null;
      const sev =
        typeof o.severity === "string" ? o.severity.trim() : undefined;
      const linkId =
        typeof o.id === "string" && isUuidString(o.id)
          ? o.id.trim()
          : undefined;
      const delAt = o.deleted_at;
      use_cases = [
        {
          id: linkId,
          use_case_id: flatUcId,
          ...(ucName !== undefined && ucName !== "" ? { use_case_name: ucName } : {}),
          zone_profile_id: zp,
          ...(sev !== undefined && sev !== "" ? { severity: sev } : {}),
          deleted_at:
            delAt === null || delAt === undefined
              ? null
              : String(delAt),
          ...(profileCount !== undefined && !Number.isNaN(profileCount)
            ? { profile_count: profileCount }
            : {}),
        },
      ];
    }
  } else if (use_cases.length > 0) {
    const topPcRaw = o.profile_count;
    const topProfileCount =
      typeof topPcRaw === "number" && Number.isFinite(topPcRaw)
        ? topPcRaw
        : typeof topPcRaw === "string" && topPcRaw.trim() !== ""
          ? Number.parseInt(topPcRaw, 10)
          : undefined;
    const topName =
      typeof o.use_case_name === "string" ? o.use_case_name.trim() : "";
    use_cases = use_cases.map((uc, i) => {
      if (i !== 0) return uc;
      const next = { ...uc };
      if (
        (next.profile_count === undefined || next.profile_count === null) &&
        topProfileCount !== undefined &&
        !Number.isNaN(topProfileCount)
      ) {
        next.profile_count = topProfileCount;
      }
      if (
        (!next.use_case_name || next.use_case_name.trim() === "") &&
        topName
      ) {
        next.use_case_name = topName;
      }
      return next;
    });
  }

  const merged = {
    ...o,
    camera_id,
    zone_id,
    use_cases,
    is_active: Boolean(o.is_active),
  } as CameraAiConfigItem & Record<string, unknown>;
  const resolvedId = resolveCameraAiConfigRowId(merged);

  return {
    id: resolvedId,
    camera_id,
    zone_id,
    zone_name: (o.zone_name ?? null) as string | null | undefined,
    is_active: Boolean(o.is_active),
    use_cases,
  };
}

/** Normalize GET /camera-ai-configs JSON (`{ data: [...] }` or raw array). */
export function normalizeCameraAiConfigResponseBody(
  raw: unknown,
): CameraAiConfigItem[] {
  let arr: unknown[] = [];
  if (Array.isArray(raw)) arr = raw;
  else if (raw && typeof raw === "object") {
    const d = (raw as { data?: unknown }).data;
    if (Array.isArray(d)) arr = d;
  }
  return arr
    .map((item) => normalizeCameraAiConfigItem(item))
    .filter((x): x is CameraAiConfigItem => x != null);
}

export function normalizeCameraAiConfigItemsList(
  list: readonly CameraAiConfigItem[],
): CameraAiConfigItem[] {
  return list
    .map((item) => normalizeCameraAiConfigItem(item))
    .filter((x): x is CameraAiConfigItem => x != null);
}

/** Item from GET /camera-ai-configs (one per zone with use_cases). API may include zone_name, use_case_name, profile_count. */
export interface CameraAiConfigItem {
  id?: string;
  camera_id: string;
  zone_id: string;
  /** When null from API, AI Models tab shows blank (no fallback to zone_id). */
  zone_name?: string | null;
  is_active: boolean;
  /** One API row per zone; each entry here becomes its own table row. */
  use_cases: Array<{
    /** Per–use-case link row id (used for PUT when parent has no `id`). */
    id?: string;
    use_case_id: string;
    use_case_name?: string;
    zone_profile_id?: string | null;
    severity?: string;
    deleted_at?: string | null;
    profile_count?: number;
  }>;
}

/**
 * Map API severity (critical | high | medium | low) to an internal 0–100 score so
 * {@link getSeverityLabel} shows the matching band (Critical ≥80, High ≥50, Medium ≥30, Low).
 */
function severityApiToScore(api: string | undefined): number {
  const s = typeof api === "string" ? api.trim().toLowerCase() : "";
  if (s === "critical") return 95;
  if (s === "high") return 65;
  if (s === "medium") return 40;
  if (s === "low") return 20;
  return 40;
}

/** Zone column label: explicit API null → blank; otherwise use name or drawn-zone / id fallbacks. */
function resolveZoneNameForAiModelRow(
  config: CameraAiConfigItem,
  zones: DrawnZone[],
): string {
  if (config.zone_name === null) return "";
  if (typeof config.zone_name === "string" && config.zone_name.trim() !== "") {
    return config.zone_name.trim();
  }
  return (
    zones.find((z) => z.id === config.zone_id)?.name ?? config.zone_id ?? ""
  );
}

/**
 * Transform GET /camera-ai-configs items to table rows.
 * One zone with N use_cases → N rows (same zone, different use case / profile / severity).
 */
function configsToModels(
  configs: CameraAiConfigItem[],
  zones: DrawnZone[],
  useCasesList: Array<{ id: string; name: string }>,
): ZoneAIModel[] {
  return configs.flatMap((config, configIndex) => {
    const zoneName = resolveZoneNameForAiModelRow(config, zones);
    const parentConfigId = resolveCameraAiConfigRowId(
      config as CameraAiConfigItem & Record<string, unknown>,
    );

    const useCaseRows = (config.use_cases ?? []).filter(
      (uc) => uc != null && !uc.deleted_at,
    );

    const rowFromUseCase = (
      uc: CameraAiConfigItem["use_cases"][number],
      uIndex: number,
    ): ZoneAIModel => {
      const useCaseName =
        uc.use_case_name ??
        useCasesList.find((u) => u.id === uc.use_case_id)?.name ??
        uc.use_case_id ??
        "";
      const useCaseRowId =
        typeof uc.id === "string" && isUuidString(uc.id)
          ? uc.id.trim()
          : undefined;
      /** PUT: parent camera-ai-config id, or per–use-case row id when API omits parent `id`. */
      const configIdForPut = parentConfigId ?? useCaseRowId;
      const stableRowKey =
        useCaseRowId ??
        (parentConfigId && uc.use_case_id
          ? `${parentConfigId}-${uc.use_case_id}`
          : `${config.zone_id}-${uc.use_case_id}-${uIndex}-${configIndex}`);

      return {
        id: stableRowKey,
        zoneName,
        aiModel: useCaseName || "—",
        confidenceScore: severityApiToScore(uc.severity),
        objectTypes: [],
        createdAt: new Date(),
        configId: configIdForPut,
        zoneId: config.zone_id,
        useCaseId: uc.use_case_id,
        zoneProfileId: uc.zone_profile_id ?? null,
        isActive: config.is_active,
        profileCount: uc.profile_count,
      };
    };

    if (useCaseRows.length === 0) {
      const resolvedConfigId = parentConfigId;
      const stableRowKey =
        resolvedConfigId ?? `${config.zone_id}-${configIndex}`;
      return [
        {
          id: stableRowKey,
          zoneName,
          aiModel: "—",
          confidenceScore: severityApiToScore(undefined),
          objectTypes: [],
          createdAt: new Date(),
          configId: resolvedConfigId,
          zoneId: config.zone_id,
          useCaseId: undefined,
          zoneProfileId: null,
          isActive: config.is_active,
          profileCount: undefined,
        },
      ];
    }

    return useCaseRows.map((uc, uIndex) => rowFromUseCase(uc, uIndex));
  });
}

interface AIModelsTabProps {
  zones: DrawnZone[];
  /** Camera id for POST /camera-ai-configs (e.g. cameraZone.id from Zone Configuration) */
  cameraId?: string;
  /** Optional: map use case display name to API use_case_id for POST body */
  useCaseOptions?: Array<{ id: string; name: string }>;
  onAddModel?: (model: ZoneAIModel) => void;
  onEditModel?: (model: ZoneAIModel) => void;
  onDeleteModel?: (modelId: string) => void;
  /** Fetched when AI Models tab is opened (GET /camera-ai-configs) */
  cameraAiConfigs?: CameraAiConfigItem[];
  cameraAiConfigsLoading?: boolean;
  cameraAiConfigsError?: string | null;
  /** Called after a config is created so parent can refetch configs */
  onConfigCreated?: () => void;
  /**
   * When set, POST /cameras with this body runs before POST /camera-ai-configs/ or
   * PUT /camera-ai-configs/{id} when the user clicks Save on an add or edit row.
   */
  cameraSyncPayload?: PostCamerasCreateBody | null;
  /** Refetch GET /cameras/{id}/zones (e.g. when opening add row so Zone dropdown is current). */
  onRequestZonesRefresh?: () => void | Promise<void>;
  /**
   * When true (e.g. Zone Configuration from "View Details"), no add / edit / delete / object-type edit.
   * @default false
   */
  readOnly?: boolean;
}

const AI_MODELS = [
  "Face Detection",
  "Object Detection",
  "Fire & Smoke",
  "Intrusion Detection",
  "PPE",
  "Crowd Detection",
  "Vehicle Detection",
];

const OBJECT_TYPES = [
];

/** GET /zone-profiles/zone/{zone_id} response item */
export interface ZoneProfileApiRecord {
  id: string;
  profiles?: string | Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
  /** Seconds; optional until backend exposes it */
  cooldown_interval?: number;
}

type ObjectTypeDialogRow = {
  name: string;
  enabled: boolean;
  confidence: number;
  threshold: number;
};

function parseProfilesField(
  profiles: string | Record<string, unknown> | undefined,
): unknown {
  if (profiles == null) return null;
  if (typeof profiles === "string") {
    try {
      return JSON.parse(profiles) as unknown;
    } catch {
      return null;
    }
  }
  return profiles;
}

/** Extract items from a zone profile record (POST body shape: { items: [{ target, confidence, threshold }] }). */
function extractProfileItemsFromRecord(
  record: ZoneProfileApiRecord | undefined,
): Array<{
  target: string;
  confidence: number;
  threshold: number;
}> {
  const parsed = parseProfilesField(record?.profiles);
  if (!parsed || typeof parsed !== "object") return [];
  const items = (parsed as { items?: unknown }).items;
  if (!Array.isArray(items)) return [];
  const out: Array<{ target: string; confidence: number; threshold: number }> =
    [];
  for (const raw of items) {
    if (!raw || typeof raw !== "object") continue;
    const o = raw as Record<string, unknown>;
    const name = String(o.target ?? o.name ?? "").trim();
    if (!name) continue;
    const rawConf =
      typeof o.confidence === "number"
        ? o.confidence
        : Number(o.confidence) || 0.5;
    const confidence = clampObjectTypeDialogConfidence(rawConf);
    const threshold = normalizeObjectTypeDialogThreshold(o.threshold);
    out.push({ target: name, confidence, threshold });
  }
  return out;
}

function findProfileRecordForModel(
  records: ZoneProfileApiRecord[],
  zoneProfileId: string | null | undefined,
): ZoneProfileApiRecord | undefined {
  if (zoneProfileId && records.length > 0) {
    const hit = records.find((r) => r.id === zoneProfileId);
    if (hit) return hit;
  }
  return records[0];
}

/** Merge OBJECT_TYPES with API profile items; extras from API are appended. */
function mergeObjectTypesWithProfileItems(
  items: Array<{ target: string; confidence: number; threshold: number }>,
): ObjectTypeDialogRow[] {
  const byName = new Map<string, { confidence: number; threshold: number }>();
  for (const it of items) {
    byName.set(it.target, {
      confidence: clampObjectTypeDialogConfidence(it.confidence),
      threshold: normalizeObjectTypeDialogThreshold(it.threshold),
    });
  }
  const rows: ObjectTypeDialogRow[] = [];
  for (const name of OBJECT_TYPES) {
    const spec = byName.get(name);
    rows.push({
      name,
      enabled: spec != null,
      confidence: clampObjectTypeDialogConfidence(spec?.confidence ?? 0.5),
      threshold: normalizeObjectTypeDialogThreshold(spec?.threshold ?? 1),
    });
  }
  for (const [name, spec] of byName) {
    if (!OBJECT_TYPES.includes(name)) {
      rows.push({
        name,
        enabled: true,
        confidence: clampObjectTypeDialogConfidence(spec.confidence),
        threshold: normalizeObjectTypeDialogThreshold(spec.threshold),
      });
    }
  }
  return rows;
}

/** Allowed range for Object Types dialog confidence (POST /zone-profiles items). */
const OBJECT_TYPE_DIALOG_CONFIDENCE_MIN = 0.3;
const OBJECT_TYPE_DIALOG_CONFIDENCE_MAX = 1;

/** Zone profile item threshold: integer, minimum 1. */
const OBJECT_TYPE_DIALOG_THRESHOLD_MIN = 1;

/** Cooldown interval (seconds) in Object Types dialog — range aligned with alert cooldown APIs. */
const COOLDOWN_INTERVAL_MIN = 0;
const COOLDOWN_INTERVAL_MAX = 3600;

function clampCooldownIntervalSeconds(value: number): number {
  if (!Number.isFinite(value)) return COOLDOWN_INTERVAL_MIN;
  return Math.min(
    COOLDOWN_INTERVAL_MAX,
    Math.max(COOLDOWN_INTERVAL_MIN, Math.round(value)),
  );
}

function readCooldownIntervalFromProfileRecord(
  record: ZoneProfileApiRecord | undefined,
): number {
  if (!record) return COOLDOWN_INTERVAL_MIN;
  const r = record as Record<string, unknown>;
  const keys = [
    "cooldown_interval",
    "cooldown_seconds",
    "countdown_interval",
  ] as const;
  for (const k of keys) {
    const v = r[k];
    if (typeof v === "number" && Number.isFinite(v)) {
      return clampCooldownIntervalSeconds(v);
    }
    if (typeof v === "string" && v.trim() !== "") {
      const p = parseInt(v, 10);
      if (!Number.isNaN(p)) return clampCooldownIntervalSeconds(p);
    }
  }
  return COOLDOWN_INTERVAL_MIN;
}

function clampObjectTypeDialogConfidence(value: number): number {
  if (!Number.isFinite(value)) return OBJECT_TYPE_DIALOG_CONFIDENCE_MIN;
  return Math.min(
    OBJECT_TYPE_DIALOG_CONFIDENCE_MAX,
    Math.max(OBJECT_TYPE_DIALOG_CONFIDENCE_MIN, value),
  );
}

/** Zone profile item threshold: integer ≥ {@link OBJECT_TYPE_DIALOG_THRESHOLD_MIN}, no decimals. */
function normalizeObjectTypeDialogThreshold(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return OBJECT_TYPE_DIALOG_THRESHOLD_MIN;
  return Math.max(OBJECT_TYPE_DIALOG_THRESHOLD_MIN, Math.round(n));
}

function defaultObjectTypesDialogRows(): ObjectTypeDialogRow[] {
  return OBJECT_TYPES.map((name) => ({
    name,
    enabled: true,
    confidence: clampObjectTypeDialogConfidence(0.5),
    threshold: 1,
  }));
}

/** GET /zone-profiles/zone/{zone_id} — all zone profile records for a zone. */
export async function fetchZoneProfilesByZoneId(
  zoneId: string,
): Promise<ZoneProfileApiRecord[]> {
  const base = API_BASE.replace(/\/$/, "");
  if (!base) throw new Error("API base not configured.");
  const url = `${base}/zone-profiles/zone/${encodeURIComponent(zoneId)}`;
  const res = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json", ...userTimezoneHeaders() },
  });
  if (!res.ok) throw new Error(res.statusText || `HTTP ${res.status}`);
  const body: unknown = await res.json();
  let arr: unknown[] = [];
  if (Array.isArray(body)) arr = body;
  else if (body && typeof body === "object") {
    const d = (body as { data?: unknown }).data;
    if (Array.isArray(d)) arr = d;
  }
  return arr.filter(
    (x): x is ZoneProfileApiRecord =>
      x != null &&
      typeof x === "object" &&
      typeof (x as ZoneProfileApiRecord).id === "string",
  ) as ZoneProfileApiRecord[];
}

/** GET /zone-profiles/use_case_classes/{use_case_id} — allowed object classes for a use case. */
async function fetchUseCaseClasses(useCaseId: string): Promise<unknown> {
  const base = API_BASE.replace(/\/$/, "");
  if (!base) throw new Error("API base not configured.");
  const url = `${base}/zone-profiles/use_case_classes/${encodeURIComponent(useCaseId)}`;
  const res = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json", ...userTimezoneHeaders() },
  });
  if (!res.ok) throw new Error(res.statusText || `HTTP ${res.status}`);
  return res.json();
}

/**
 * Normalize use-case classes response (object map, { data }, or array) to display names for the dialog.
 */
function extractClassNamesFromUseCaseClassesBody(body: unknown): string[] {
  if (body == null) return [];
  if (Array.isArray(body)) {
    const names: string[] = [];
    for (const el of body) {
      if (typeof el === "string" && el.trim()) names.push(el.trim());
      else if (el && typeof el === "object" && !Array.isArray(el)) {
        const o = el as Record<string, unknown>;
        const n =
          (typeof o.name === "string" && o.name.trim()) ||
          (typeof o.class_name === "string" && o.class_name.trim()) ||
          (typeof o.target === "string" && o.target.trim()) ||
          (typeof o.label === "string" && o.label.trim());
        if (n) names.push(n);
      }
    }
    return [...new Set(names)];
  }
  if (typeof body !== "object") return [];
  let obj = body as Record<string, unknown>;
  const data = obj.data;
  if (data != null && typeof data === "object" && !Array.isArray(data)) {
    obj = data as Record<string, unknown>;
  } else if (Array.isArray(data)) {
    return extractClassNamesFromUseCaseClassesBody(data);
  }
  const skip = new Set(["message", "status", "detail", "error"]);
  const names: string[] = [];
  for (const [k, v] of Object.entries(obj)) {
    if (skip.has(k)) continue;
    if (v != null && typeof v === "object" && !Array.isArray(v)) {
      const vo = v as Record<string, unknown>;
      const label =
        (typeof vo.name === "string" && vo.name.trim()) ||
        (typeof vo.class_name === "string" && vo.class_name.trim()) ||
        (typeof vo.label === "string" && vo.label.trim());
      names.push(label || k);
    } else if (typeof v === "string" && v.trim()) {
      names.push(v.trim());
    } else {
      names.push(k);
    }
  }
  return [...new Set(names)];
}

/** Merge saved profile items with an ordered list of class names from GET use_case_classes. */
function mergeProfileItemsIntoClassRows(
  orderedClassNames: string[],
  items: Array<{ target: string; confidence: number; threshold: number }>,
): ObjectTypeDialogRow[] {
  const byName = new Map<
    string,
    { confidence: number; threshold: number }
  >();
  for (const it of items) {
    byName.set(it.target, {
      confidence: clampObjectTypeDialogConfidence(it.confidence),
      threshold: normalizeObjectTypeDialogThreshold(it.threshold),
    });
  }
  const rows: ObjectTypeDialogRow[] = [];
  for (const name of orderedClassNames) {
    const spec = byName.get(name);
    rows.push({
      name,
      enabled: spec != null,
      confidence: clampObjectTypeDialogConfidence(spec?.confidence ?? 0.5),
      threshold: normalizeObjectTypeDialogThreshold(spec?.threshold ?? 1),
    });
  }
  for (const it of items) {
    if (!orderedClassNames.includes(it.target)) {
      rows.push({
        name: it.target,
        enabled: true,
        confidence: clampObjectTypeDialogConfidence(it.confidence),
        threshold: normalizeObjectTypeDialogThreshold(it.threshold),
      });
    }
  }
  return rows;
}

/** Map confidence score (0-100) to severity band for display (matches screenshot). */
function getSeverityLabel(confidenceScore: number): string {
  if (confidenceScore >= 80) return "8-10 Critical";
  if (confidenceScore >= 50) return "5-8 High";
  if (confidenceScore >= 30) return "3-5 Medium";
  return "1-3 Low";
}

const SEVERITY_OPTIONS = ["8-10 Critical", "5-8 High", "3-5 Medium", "1-3 Low"];

/** Severity pill background and text color by label. */
function getSeverityChipStyle(label: string): {
  bgcolor: string;
  color: string;
} {
  if (label === "8-10 Critical") return { bgcolor: "#B71C1C", color: "#fff" };
  if (label === "5-8 High") return { bgcolor: "#C62828", color: "#fff" };
  if (label === "3-5 Medium") return { bgcolor: "#E53935", color: "#fff" };
  return { bgcolor: "#FF7043", color: "#fff" };
}

/** Map severity dropdown label to API value for POST /camera-ai-configs: send critical | high | medium | low. */
function mapSeverityToApi(label: string | undefined): string {
  if (label === "8-10 Critical") return "critical";
  if (label === "5-8 High") return "high";
  if (label === "3-5 Medium") return "medium";
  if (label === "1-3 Low") return "low";
  return "medium";
}

export function AIModelsTab({
  zones,
  cameraId = "",
  useCaseOptions = [],
  onAddModel,
  onEditModel,
  onDeleteModel,
  cameraAiConfigs = [],
  cameraAiConfigsLoading = false,
  cameraAiConfigsError = null,
  onConfigCreated,
  cameraSyncPayload = null,
  onRequestZonesRefresh,
  readOnly = false,
}: AIModelsTabProps) {
  /** Filled from GET /camera-ai-configs via useEffect (each row needs a real config UUID for PUT). */
  const [models, setModels] = useState<ZoneAIModel[]>([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterUseCaseName, setFilterUseCaseName] = useState<string>("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(14);
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<AiModelOrderBy>("name");
  /** Name-column sort arrows only after user clicks Name (same as Zones tab). */
  const [nameSortEngaged, setNameSortEngaged] = useState(false);
  const [anchorEl, setAnchorEl] = useState<{
    model: ZoneAIModel;
    mouseX: number;
    mouseY: number;
  } | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [modelToDelete, setModelToDelete] = useState<ZoneAIModel | null>(null);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  /** While PUT is_active runs for a row, disables that row's toggle. */
  const [activeToggleSavingId, setActiveToggleSavingId] = useState<string | null>(
    null,
  );
  const [objectTypesDialogOpen, setObjectTypesDialogOpen] = useState(false);
  const [objectTypesDialogModel, setObjectTypesDialogModel] =
    useState<ZoneAIModel | null>(null);
  /** When true, dialog was opened from add-row Save button; on OK we apply selection to newModel then run save. */
  const [objectTypesDialogForNewModel, setObjectTypesDialogForNewModel] =
    useState(false);
  const [objectTypesDialogRows, setObjectTypesDialogRows] = useState<
    Array<{
      name: string;
      enabled: boolean;
      confidence: number;
      threshold: number;
    }>
  >([]);
  const [objectTypesDialogSearch, setObjectTypesDialogSearch] = useState("");
  const [objectTypesDialogLoading, setObjectTypesDialogLoading] =
    useState(false);
  const [objectTypesDialogError, setObjectTypesDialogError] = useState<
    string | null
  >(null);
  /** After POST /zone-profiles from dialog Ok, count to show on Object Type button. */
  const [objectTypeConfigCount, setObjectTypeConfigCount] = useState<
    number | null
  >(null);
  /** Zone profile id from POST /zone-profiles; sent as zone_profile_id when user clicks Save. */
  const [createdZoneProfileId, setCreatedZoneProfileId] = useState<
    string | null
  >(null);
  /** Resolved profile UUID for PUT /zone-profiles/{profile_id} when editing object types (from GET zone profiles or model.zoneProfileId). */
  const [
    objectTypesDialogProfileIdForPut,
    setObjectTypesDialogProfileIdForPut,
  ] = useState<string | null>(null);
  /** True while POST /zone-profiles or PUT /zone-profiles/{id} runs from dialog Ok. */
  const [objectTypesDialogOkLoading, setObjectTypesDialogOkLoading] =
    useState(false);
  /** Cooldown interval (seconds), 0–3600; sent as `cooldown_interval` on zone profile POST/PUT. */
  const [objectTypesDialogCooldownSeconds, setObjectTypesDialogCooldownSeconds] =
    useState(COOLDOWN_INTERVAL_MIN);

  const [editingModel, setEditingModel] = useState<ZoneAIModel | null>(null);
  const [editForm, setEditForm] = useState<{
    zoneId: string;
    useCaseId: string;
    zoneProfileId: string;
    severityLabel: string;
    isActive: boolean;
  }>({
    zoneId: "",
    useCaseId: "",
    zoneProfileId: "",
    severityLabel: "",
    isActive: true,
  });
  const [editSaveError, setEditSaveError] = useState<string | null>(null);
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    if (!readOnly) return;
    setIsAddingNew(false);
    setEditingModel(null);
    setAnchorEl(null);
    setObjectTypesDialogOpen(false);
  }, [readOnly]);

  /** Use cases from GET /use-cases/ for correct use_case_id in POST */
  const [useCasesList, setUseCasesList] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [useCasesLoading, setUseCasesLoading] = useState(false);
  const [useCasesError, setUseCasesError] = useState<string | null>(null);

  const fetchUseCases = useCallback(async () => {
    const base = API_BASE.replace(/\/$/, "");
    if (!base) return;
    setUseCasesLoading(true);
    setUseCasesError(null);
    try {
      const res = await fetch(`${base}/use-cases/`, {
        headers: { Accept: "application/json", ...userTimezoneHeaders() },
      });
      if (!res.ok) throw new Error(res.statusText || `HTTP ${res.status}`);
      const body: unknown = await res.json();
      const data = extractUseCasesFromResponse(body);
      const list = data
        .filter(
          (item): item is { id: string; name?: string } =>
            item != null && typeof item === "object" && "id" in item,
        )
        .map((item) => ({ id: String(item.id), name: item.name ?? item.id }));
      setUseCasesList(list);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load use cases";
      setUseCasesError(message);
      setUseCasesList([]);
      toast.error(message, TOAST_ERROR_POSITION);
    } finally {
      setUseCasesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUseCases();
  }, [fetchUseCases]);

  /** Sync table rows from API configs; normalize nested shapes so config row `id` is found for PUT. */
  useEffect(() => {
    const normalized =
      cameraAiConfigs.length > 0
        ? normalizeCameraAiConfigItemsList(cameraAiConfigs)
        : [];
    setModels(configsToModels(normalized, zones, useCasesList));
  }, [cameraAiConfigs, zones, useCasesList]);

  // New model form state
  const [newModel, setNewModel] = useState<{
    zoneName: string;
    useCaseId: string;
    aiModel: string;
    confidenceScore: string;
    objectTypes: string[];
    severityLabel?: string;
  }>({
    zoneName: "",
    useCaseId: "",
    aiModel: "",
    confidenceScore: "",
    objectTypes: [],
  });

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    model: ZoneAIModel,
  ) => {
    if (readOnly) return;
    event.stopPropagation();
    setAnchorEl({
      model,
      mouseX: event.clientX,
      mouseY: event.clientY,
    });
  };

  const handleRowContextMenu = (
    event: React.MouseEvent,
    model: ZoneAIModel,
  ) => {
    if (readOnly) return;
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl({
      model,
      mouseX: event.clientX,
      mouseY: event.clientY,
    });
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  /** Same zone / use-case / config row id resolution as {@link handleEditSave} for PUT. */
  const resolveModelPutContext = useCallback(
    (model: ZoneAIModel) => {
      const camId = cameraId.trim();
      const zoneIdStr = (
        model.zoneId?.trim() ||
        zones.find((z) => z.name === model.zoneName)?.id ||
        ""
      ).trim();
      const useCaseIdStr = (
        model.useCaseId?.trim() ||
        useCasesList.find((u) => u.name === model.aiModel)?.id ||
        ""
      ).trim();
      const rawPathId =
        model.configId && isUuidString(model.configId)
          ? model.configId.trim()
          : model.id && isUuidString(model.id)
            ? model.id.trim()
            : "";
      const configRowIdForPut =
        rawPathId && zoneIdStr && rawPathId !== zoneIdStr ? rawPathId : "";
      return { camId, zoneIdStr, useCaseIdStr, configRowIdForPut };
    },
    [cameraId, zones, useCasesList],
  );

  const getToggleDisabledReason = useCallback(
    (model: ZoneAIModel): string | null => {
      if (readOnly) return "Read-only.";
      const { camId, zoneIdStr, useCaseIdStr, configRowIdForPut } =
        resolveModelPutContext(model);
      if (!camId || !isUuidString(camId))
        return "Camera id is missing or invalid.";
      if (!isUuidString(zoneIdStr) || !isUuidString(useCaseIdStr))
        return "Zone or use case could not be resolved.";
      if (!configRowIdForPut)
        return "Config id missing; refresh the list.";
      return null;
    },
    [readOnly, resolveModelPutContext],
  );

  const handleToggleModelActive = useCallback(
    async (model: ZoneAIModel, nextActive: boolean) => {
      if (readOnly) return;
      const { configRowIdForPut } = resolveModelPutContext(model);
      if (!configRowIdForPut || !isUuidString(configRowIdForPut)) {
        return;
      }

      const base = API_BASE.replace(/\/$/, "");
      if (!base) return;

      const prevActive = model.isActive ?? true;

      setModels((prev) =>
        prev.map((m) =>
          m.id === model.id ? { ...m, isActive: nextActive } : m,
        ),
      );
      setActiveToggleSavingId(model.id);
      try {
        const patchBody: PatchCameraAiConfigToggleBody = {
          id: configRowIdForPut,
          is_active: nextActive,
        };
        const res = await fetch(`${base}/camera-ai-configs/toggle`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...userTimezoneHeaders(),
          },
          body: JSON.stringify(patchBody),
        });
        if (!res.ok) {
          const text = await res.text();
          let message = `Failed to toggle config (${res.status})`;
          try {
            const json = JSON.parse(text) as {
              detail?: string | Array<{ msg?: string }>;
              message?: string;
              error?: string;
            };
            if (json.detail && Array.isArray(json.detail)) {
              message =
                json.detail
                  .map((d) => d.msg)
                  .filter(Boolean)
                  .join("; ") || message;
            } else if (typeof json.detail === "string") message = json.detail;
            else if (typeof json.message === "string" && json.message.trim())
              message = json.message;
            else if (typeof json.error === "string" && json.error.trim())
              message = json.error;
          } catch {
            if (text) message = text.slice(0, 200);
          }
          throw new Error(message);
        }
        onConfigCreated?.();
      } catch (err) {
        setModels((prev) =>
          prev.map((m) =>
            m.id === model.id ? { ...m, isActive: prevActive } : m,
          ),
        );
        const message =
          err instanceof Error ? err.message : "Failed to toggle config.";
        toast.error(message, TOAST_ERROR_POSITION);
      } finally {
        setActiveToggleSavingId(null);
      }
    },
    [readOnly, resolveModelPutContext, onConfigCreated],
  );

  const handleEdit = () => {
    if (readOnly) return;
    const model = anchorEl?.model;
    handleMenuClose();
    if (!model) return;
    setIsAddingNew(false);
    setEditingModel(model);
    const zoneId =
      model.zoneId ?? zones.find((z) => z.name === model.zoneName)?.id ?? "";
    const useCaseId =
      model.useCaseId ??
      useCasesList.find((u) => u.name === model.aiModel)?.id ??
      "";
    setEditForm({
      zoneId,
      useCaseId,
      zoneProfileId: model.zoneProfileId ?? "",
      severityLabel: getSeverityLabel(model.confidenceScore),
      isActive: model.isActive ?? true,
    });
    setEditSaveError(null);
    if (zoneId) {
      void fetchZoneProfilesByZoneId(zoneId).catch(() => {
        /* prefetch only; Object Types dialog refetches on open */
      });
    }
  };

  const handleEditCancel = () => {
    setEditingModel(null);
    setEditSaveError(null);
  };

  const handleEditSave = async () => {
    if (!editingModel || !cameraId) return;
    const base = API_BASE.replace(/\/$/, "");
    if (!base) {
      setEditSaveError("API base not configured.");
      return;
    }
    if (!editForm.zoneId?.trim() || !editForm.useCaseId?.trim()) {
      setEditSaveError("Please select zone and use case.");
      return;
    }

    const camId = cameraId.trim();
    const zoneIdStr = editForm.zoneId.trim();
    const useCaseIdStr = editForm.useCaseId.trim();
    if (!isUuidString(camId) || !isUuidString(zoneIdStr) || !isUuidString(useCaseIdStr)) {
      setEditSaveError(
        "Camera, zone, and use case must be valid IDs to save.",
      );
      return;
    }

    /**
     * PUT path must be the camera_ai_configs primary key, not zone_id.
     * If we only have zone_id or no UUID, use POST /camera-ai-configs/ (create configs by zone).
     */
    const rawPathId =
      editingModel.configId && isUuidString(editingModel.configId)
        ? editingModel.configId.trim()
        : editingModel.id && isUuidString(editingModel.id)
          ? editingModel.id.trim()
          : "";
    const configRowIdForPut =
      rawPathId && rawPathId !== zoneIdStr ? rawPathId : "";

    const zoneProfileTrimmed = editForm.zoneProfileId?.trim() ?? "";
    const severityApi = mapSeverityToApi(editForm.severityLabel);

    setEditSaveError(null);
    setEditSaving(true);
    try {
      if (cameraSyncPayload != null) {
        await postCamerasCreate(base, cameraSyncPayload);
      }

      if (configRowIdForPut) {
        const putBody: PutCameraAiConfigBody = {
          config_id: configRowIdForPut,
          camera_id: camId,
          use_case_id: useCaseIdStr,
          zone_id: zoneIdStr,
          zone_profile_id: zoneProfileTrimmed !== "" ? zoneProfileTrimmed : null,
          severity: severityApi,
          is_active: editForm.isActive,
          detection_cooldown_interval: 0,
        };
        const res = await fetch(
          `${base}/camera-ai-configs/${encodeURIComponent(configRowIdForPut)}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              ...userTimezoneHeaders(),
            },
            body: JSON.stringify(putBody),
          },
        );
        if (!res.ok) {
          const text = await res.text();
          let message = `Failed to update config (${res.status})`;
          try {
            const json = JSON.parse(text) as {
              detail?: string | Array<{ msg?: string }>;
              message?: string;
              error?: string;
              status?: string;
            };
            if (json.detail && Array.isArray(json.detail)) {
              message =
                json.detail
                  .map((d) => d.msg)
                  .filter(Boolean)
                  .join("; ") || message;
            } else if (typeof json.detail === "string") message = json.detail;
            else if (typeof json.message === "string" && json.message.trim())
              message = json.message;
            else if (typeof json.error === "string" && json.error.trim())
              message = json.error;
          } catch {
            if (text) message = text.slice(0, 200);
          }
          throw new Error(message);
        }
      } else {
        const postBody: PostCameraAiConfigBody = {
          camera_id: camId,
          use_case_id: useCaseIdStr,
          zone_id: zoneIdStr,
          zone_profile_id:
            zoneProfileTrimmed !== "" ? zoneProfileTrimmed : null,
          severity: severityApi,
          detection_cooldown_interval: 0,
          is_active: editForm.isActive,
        };
        await postCameraAiConfigsCreate(base, postBody);
      }

      onConfigCreated?.();
      handleEditCancel();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to save config.";
      setEditSaveError(message);
      toast.error(message, TOAST_ERROR_POSITION);
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = () => {
    if (readOnly) return;
    if (anchorEl?.model) {
      setModelToDelete(anchorEl.model);
      setDeleteError(null);
      setDeleteDialogOpen(true);
    }
    handleMenuClose();
  };

  /** DELETE /camera-ai-configs/ — bulk soft-delete by `camera_ai_configs` row id(s) (recycle bin). */
  const confirmDelete = async () => {
    if (!modelToDelete) return;
    const configRowId = modelToDelete.configId?.trim();
    if (!configRowId) {
      setDeleteError(
        "Config id is missing; cannot delete. Open the AI Models tab again to refresh.",
      );
      return;
    }
    const zoneId = modelToDelete.zoneId?.trim();
    const base = API_BASE.replace(/\/$/, "");
    if (!base) {
      setDeleteError("API base not configured.");
      return;
    }
    setDeleteError(null);
    setDeleteSaving(true);
    try {
      const res = await fetch(`${base}/camera-ai-configs/`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...userTimezoneHeaders(),
        },
        body: JSON.stringify({ ids: [configRowId] }),
      });
      if (!res.ok) {
        const text = await res.text();
        let message = `Delete failed (${res.status})`;
        try {
          const json = JSON.parse(text) as {
            detail?: string | Array<{ msg?: string }>;
          };
          if (Array.isArray(json.detail)) {
            message =
              json.detail
                .map((d) => d.msg)
                .filter(Boolean)
                .join("; ") || message;
          } else if (typeof json.detail === "string") {
            message = json.detail;
          }
        } catch {
          if (text) message = text.slice(0, 200);
        }
        throw new Error(message);
      }
      void fetchAndApplyRecycleBinTotals();
      onDeleteModel?.(modelToDelete.id);
      onConfigCreated?.();
      setDeleteDialogOpen(false);
      setModelToDelete(null);
      if (zoneId && editingModel?.zoneId === zoneId) {
        handleEditCancel();
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to delete.";
      setDeleteError(message);
      toast.error(message, TOAST_ERROR_POSITION);
    } finally {
      setDeleteSaving(false);
    }
  };

  const handleSort = (property: AiModelOrderBy) => {
    if (property === "name") setNameSortEngaged(true);
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleOpenObjectTypesDialog = async (model: ZoneAIModel) => {
    if (readOnly) return;
    setObjectTypesDialogModel(model);
    setObjectTypesDialogForNewModel(false);
    setObjectTypesDialogSearch("");
    setObjectTypesDialogError(null);
    setObjectTypesDialogProfileIdForPut(null);
    setObjectTypesDialogCooldownSeconds(COOLDOWN_INTERVAL_MIN);
    setObjectTypesDialogOpen(true);

    const useCaseId = model.useCaseId?.trim() ?? "";
    const fetchClassesPromise =
      useCaseId && isUuidString(useCaseId)
        ? fetchUseCaseClasses(useCaseId)
            .then((body) => extractClassNamesFromUseCaseClassesBody(body))
            .catch((err) => {
              console.error(
                "[AIModelsTab] GET /zone-profiles/use_case_classes/{use_case_id} failed",
                err,
              );
              return [] as string[];
            })
        : Promise.resolve([] as string[]);

    const zoneId = model.zoneId?.trim();
    if (!zoneId) {
      const fallbackPid = model.zoneProfileId?.trim();
      setObjectTypesDialogProfileIdForPut(
        fallbackPid && isUuidString(fallbackPid) ? fallbackPid : null,
      );
      setObjectTypesDialogLoading(true);
      setObjectTypesDialogRows([]);
      try {
        const classNames = await fetchClassesPromise;
        setObjectTypesDialogRows(
          classNames.length > 0
            ? classNames.map((name) => ({
                name,
                enabled: true,
                confidence: 0.5,
                threshold: 1,
              }))
            : defaultObjectTypesDialogRows(),
        );
        setObjectTypesDialogCooldownSeconds(COOLDOWN_INTERVAL_MIN);
      } finally {
        setObjectTypesDialogLoading(false);
      }
      return;
    }
    setObjectTypesDialogLoading(true);
    setObjectTypesDialogRows([]);
    try {
      const [records, classNames] = await Promise.all([
        fetchZoneProfilesByZoneId(zoneId),
        fetchClassesPromise,
      ]);
      const record = findProfileRecordForModel(records, model.zoneProfileId);
      const resolvedProfileId =
        record?.id?.trim() ??
        (model.zoneProfileId?.trim() && isUuidString(model.zoneProfileId.trim())
          ? model.zoneProfileId.trim()
          : null);
      setObjectTypesDialogProfileIdForPut(resolvedProfileId);
      const items = extractProfileItemsFromRecord(record);
      let rows: ObjectTypeDialogRow[];
      if (items.length === 0) {
        rows =
          classNames.length > 0
            ? classNames.map((name) => ({
                name,
                enabled: true,
                confidence: 0.5,
                threshold: 1,
              }))
            : defaultObjectTypesDialogRows();
      } else if (classNames.length > 0) {
        rows = mergeProfileItemsIntoClassRows(classNames, items);
      } else {
        rows = mergeObjectTypesWithProfileItems(items);
      }
      setObjectTypesDialogRows(rows);
      setObjectTypesDialogCooldownSeconds(
        readCooldownIntervalFromProfileRecord(record),
      );
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load zone profiles";
      setObjectTypesDialogError(message);
      toast.error(message, TOAST_ERROR_POSITION);
      setObjectTypesDialogRows(defaultObjectTypesDialogRows());
      const fallbackPid = model.zoneProfileId?.trim();
      setObjectTypesDialogProfileIdForPut(
        fallbackPid && isUuidString(fallbackPid) ? fallbackPid : null,
      );
    } finally {
      setObjectTypesDialogLoading(false);
    }
  };

  const handleCloseObjectTypesDialog = () => {
    setObjectTypesDialogOpen(false);
    setObjectTypesDialogModel(null);
    setObjectTypesDialogForNewModel(false);
    setObjectTypesDialogLoading(false);
    setObjectTypesDialogError(null);
    setObjectTypesDialogProfileIdForPut(null);
    setObjectTypesDialogOkLoading(false);
    setObjectTypesDialogCooldownSeconds(COOLDOWN_INTERVAL_MIN);
  };

  /** POST /zone-profiles with selected object types from dialog; on 201 sets objectTypeConfigCount and createdZoneProfileId. */
  const handlePostZoneProfiles = async () => {
    const selectedRows = objectTypesDialogRows.filter((r) => r.enabled);
    if (selectedRows.length === 0) {
      setSaveError("Please select at least one object type.");
      return;
    }
    const base = API_BASE.replace(/\/$/, "");
    if (!base) {
      setSaveError("API base not configured.");
      return;
    }
    const topLevelThreshold = normalizeObjectTypeDialogThreshold(
      selectedRows[0]?.threshold ?? 1,
    );
    const body = {
      threshold: topLevelThreshold,
      cooldown_interval: clampCooldownIntervalSeconds(
        objectTypesDialogCooldownSeconds,
      ),
      profiles: {
        items: selectedRows.map((r) => ({
          target: r.name,
          confidence: clampObjectTypeDialogConfidence(r.confidence),
          threshold: normalizeObjectTypeDialogThreshold(r.threshold),
        })),
      },
    };
    setSaveError(null);
    setSaveInProgress(true);
    setObjectTypesDialogOkLoading(true);
    try {
      const res = await fetch(`${base}/zone-profiles`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...userTimezoneHeaders(),
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const text = await res.text();
        let message = `Failed to create zone profile (${res.status})`;
        try {
          const json = JSON.parse(text);
          if (json.detail && Array.isArray(json.detail)) {
            message =
              json.detail
                .map((d: { msg?: string }) => d.msg)
                .filter(Boolean)
                .join("; ") || message;
          } else if (typeof json.detail === "string") {
            message = json.detail;
          }
        } catch {
          if (text) message = text.slice(0, 200);
        }
        throw new Error(message);
      }
      const raw: {
        id?: string;
        data?: { id?: string; profiles?: { items?: unknown[] } };
        profiles?: string | { items?: unknown[] };
      } = await res.json().catch(() => ({}));
      const data = raw.data ?? raw;
      const zoneProfileId = (data as { id?: string }).id ?? raw.id;
      if (zoneProfileId) setCreatedZoneProfileId(zoneProfileId);

      const selectedZone = zones.find((z) => z.name === newModel.zoneName);
      const ucId = newModel.useCaseId?.trim() ?? "";
      if (
        !cameraId?.trim() ||
        !isUuidString(cameraId.trim()) ||
        !selectedZone?.id ||
        !isUuidString(selectedZone.id) ||
        !isUuidString(ucId)
      ) {
        throw new Error(
          "Camera, zone, and use case must be valid to create the AI configuration.",
        );
      }
      if (cameraSyncPayload != null) {
        await postCamerasCreate(base, cameraSyncPayload);
      }
      const zpidStr =
        zoneProfileId != null && String(zoneProfileId).trim() !== ""
          ? String(zoneProfileId).trim()
          : "";
      await postCameraAiConfigsCreate(base, {
        camera_id: cameraId.trim(),
        use_case_id: ucId,
        zone_id: selectedZone.id,
        zone_profile_id:
          zpidStr !== "" && isUuidString(zpidStr) ? zpidStr : null,
        severity: mapSeverityToApi(newModel.severityLabel),
        detection_cooldown_interval: clampCooldownIntervalSeconds(
          objectTypesDialogCooldownSeconds,
        ),
        is_active: true,
      });
      onConfigCreated?.();

      let count = selectedRows.length;
      const profiles =
        (data as { profiles?: { items?: unknown[] } }).profiles ?? raw.profiles;
      if (profiles != null) {
        if (
          typeof profiles === "object" &&
          Array.isArray((profiles as { items?: unknown[] }).items)
        ) {
          count = (profiles as { items: unknown[] }).items.length;
        } else if (typeof profiles === "string") {
          try {
            const parsed = JSON.parse(profiles) as { items?: unknown[] };
            if (Array.isArray(parsed?.items)) count = parsed.items.length;
          } catch {
            // keep count
          }
        }
      }
      setObjectTypeConfigCount(count);
      setNewModel((prev) => ({
        ...prev,
        objectTypes: selectedRows.map((r) => r.name),
      }));
      setObjectTypesDialogForNewModel(false);
      setObjectTypesDialogOpen(false);
      setObjectTypesDialogModel(null);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to create zone profile.";
      setSaveError(message);
      toast.error(message, TOAST_ERROR_POSITION);
    } finally {
      setSaveInProgress(false);
      setObjectTypesDialogOkLoading(false);
    }
  };

  /** PUT /zone-profiles/{profile_id} — update object types / confidence from dialog Ok (edit / table row flow). */
  const handlePutZoneProfile = async () => {
    const selectedRows = objectTypesDialogRows.filter((r) => r.enabled);
    if (selectedRows.length === 0) {
      setObjectTypesDialogError("Please select at least one object type.");
      return;
    }
    const profileId =
      objectTypesDialogProfileIdForPut?.trim() ||
      objectTypesDialogModel?.zoneProfileId?.trim() ||
      "";
    if (!profileId || !isUuidString(profileId)) {
      setObjectTypesDialogError(
        "No zone profile id is available. Save the AI model row or create a zone profile first.",
      );
      return;
    }
    const base = API_BASE.replace(/\/$/, "");
    if (!base) {
      setObjectTypesDialogError("API base not configured.");
      return;
    }
    const topLevelThreshold = normalizeObjectTypeDialogThreshold(
      selectedRows[0]?.threshold ?? 1,
    );
    const body = {
      threshold: topLevelThreshold,
      cooldown_interval: clampCooldownIntervalSeconds(
        objectTypesDialogCooldownSeconds,
      ),
      profiles: {
        items: selectedRows.map((r) => ({
          target: r.name,
          confidence: clampObjectTypeDialogConfidence(r.confidence),
          threshold: normalizeObjectTypeDialogThreshold(r.threshold),
        })),
      },
    };
    setObjectTypesDialogError(null);
    setObjectTypesDialogOkLoading(true);
    try {
      const res = await fetch(
        `${base}/zone-profiles/${encodeURIComponent(profileId)}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...userTimezoneHeaders(),
          },
          body: JSON.stringify(body),
        },
      );
      if (!res.ok) {
        const text = await res.text();
        let message = `Failed to update zone profile (${res.status})`;
        try {
          const json = JSON.parse(text) as { detail?: unknown };
          if (json.detail && Array.isArray(json.detail)) {
            message =
              (json.detail as { msg?: string }[])
                .map((d) => d.msg)
                .filter(Boolean)
                .join("; ") || message;
          } else if (typeof json.detail === "string") {
            message = json.detail;
          }
        } catch {
          if (text) message = text.slice(0, 200);
        }
        throw new Error(message);
      }

      const dialogModel = objectTypesDialogModel;
      if (dialogModel && cameraId?.trim()) {
        const camId = cameraId.trim();
        const zoneIdStr = (
          dialogModel.zoneId?.trim() ||
          zones.find((z) => z.name === dialogModel.zoneName)?.id ||
          ""
        ).trim();
        const useCaseIdStr = (
          dialogModel.useCaseId?.trim() ||
          useCasesList.find((u) => u.name === dialogModel.aiModel)?.id ||
          ""
        ).trim();
        if (
          isUuidString(camId) &&
          isUuidString(zoneIdStr) &&
          isUuidString(useCaseIdStr)
        ) {
          const rawPathId =
            dialogModel.configId && isUuidString(dialogModel.configId)
              ? dialogModel.configId.trim()
              : dialogModel.id && isUuidString(dialogModel.id)
                ? dialogModel.id.trim()
                : "";
          const configRowIdForPut =
            rawPathId && rawPathId !== zoneIdStr ? rawPathId : "";
          const severityApi = mapSeverityToApi(
            getSeverityLabel(dialogModel.confidenceScore),
          );
          const cooldown = clampCooldownIntervalSeconds(
            objectTypesDialogCooldownSeconds,
          );
          const zp =
            profileId.trim() !== "" && isUuidString(profileId.trim())
              ? profileId.trim()
              : null;

          if (cameraSyncPayload != null) {
            await postCamerasCreate(base, cameraSyncPayload);
          }

          if (configRowIdForPut) {
            const putCfgBody: PutCameraAiConfigBody = {
              config_id: configRowIdForPut,
              camera_id: camId,
              use_case_id: useCaseIdStr,
              zone_id: zoneIdStr,
              zone_profile_id: zp,
              severity: severityApi,
              is_active: dialogModel.isActive ?? true,
              detection_cooldown_interval: cooldown,
            };
            const cfgRes = await fetch(
              `${base}/camera-ai-configs/${encodeURIComponent(configRowIdForPut)}`,
              {
                method: "PUT",
                headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
                  ...userTimezoneHeaders(),
                },
                body: JSON.stringify(putCfgBody),
              },
            );
            if (!cfgRes.ok) {
              const text = await cfgRes.text();
              let message = `Failed to update AI config (${cfgRes.status})`;
              try {
                const json = JSON.parse(text) as {
                  detail?: string | Array<{ msg?: string }>;
                  message?: string;
                };
                if (json.detail && Array.isArray(json.detail)) {
                  message =
                    json.detail
                      .map((d: { msg?: string }) => d.msg)
                      .filter(Boolean)
                      .join("; ") || message;
                } else if (typeof json.detail === "string") {
                  message = json.detail;
                } else if (
                  typeof json.message === "string" &&
                  json.message.trim()
                ) {
                  message = json.message;
                }
              } catch {
                if (text) message = text.slice(0, 200);
              }
              throw new Error(message);
            }
          } else {
            await postCameraAiConfigsCreate(base, {
              camera_id: camId,
              use_case_id: useCaseIdStr,
              zone_id: zoneIdStr,
              zone_profile_id: zp,
              severity: severityApi,
              detection_cooldown_interval: cooldown,
              is_active: dialogModel.isActive ?? true,
            });
          }
        }
      }

      const selectedNames = selectedRows.map((r) => r.name);
      if (objectTypesDialogModel) {
        setModels((prev) =>
          prev.map((m) =>
            m.id === objectTypesDialogModel.id
              ? {
                  ...m,
                  objectTypes: selectedNames,
                  profileCount: selectedNames.length,
                  zoneProfileId: profileId,
                }
              : m,
          ),
        );
      }
      setEditingModel((prev) =>
        prev && objectTypesDialogModel && prev.id === objectTypesDialogModel.id
          ? {
              ...prev,
              objectTypes: selectedNames,
              profileCount: selectedNames.length,
              zoneProfileId: profileId,
            }
          : prev,
      );
      setEditForm((prev) => {
        if (
          !objectTypesDialogModel ||
          editingModel?.id !== objectTypesDialogModel.id
        ) {
          return prev;
        }
        return { ...prev, zoneProfileId: profileId };
      });
      onConfigCreated?.();
      handleCloseObjectTypesDialog();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to update zone profile.";
      setObjectTypesDialogError(message);
      toast.error(message, TOAST_ERROR_POSITION);
    } finally {
      setObjectTypesDialogOkLoading(false);
    }
  };

  /** Opens Object Types & Confidence dialog from add-row Object Type button. */
  const handleOpenObjectTypesDialogForNewModel = async () => {
    if (readOnly) return;
    setSaveError(null);
    setObjectTypesDialogCooldownSeconds(COOLDOWN_INTERVAL_MIN);
    setObjectTypesDialogModel(null);
    setObjectTypesDialogForNewModel(true);
    setObjectTypesDialogSearch("");
    setObjectTypesDialogError(null);
    setObjectTypesDialogOpen(true);

    const ucId = newModel.useCaseId?.trim() ?? "";
    if (ucId && isUuidString(ucId)) {
      setObjectTypesDialogLoading(true);
      setObjectTypesDialogRows([]);
      try {
        const body = await fetchUseCaseClasses(ucId);
        const classNames = extractClassNamesFromUseCaseClassesBody(body);
        setObjectTypesDialogRows(
          classNames.length > 0
            ? classNames.map((name) => ({
                name,
                enabled: true,
                confidence: 0.5,
                threshold: 1,
              }))
            : defaultObjectTypesDialogRows(),
        );
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "Failed to load use case classes.";
        toast.error(message, TOAST_ERROR_POSITION);
        setObjectTypesDialogRows(defaultObjectTypesDialogRows());
      } finally {
        setObjectTypesDialogLoading(false);
      }
    } else {
      setObjectTypesDialogRows(defaultObjectTypesDialogRows());
      setObjectTypesDialogLoading(false);
    }
  };

  const handleConfirmObjectTypesDialog = () => {
    if (objectTypesDialogForNewModel) {
      void handlePostZoneProfiles();
      return;
    }
    void handlePutZoneProfile();
  };

  const objectTypesDialogFilteredRows = useMemo(() => {
    if (!objectTypesDialogSearch.trim()) return objectTypesDialogRows;
    const q = objectTypesDialogSearch.toLowerCase();
    return objectTypesDialogRows.filter((r) =>
      r.name.toLowerCase().includes(q),
    );
  }, [objectTypesDialogRows, objectTypesDialogSearch]);

  const objectTypesDialogSelectAllCheckbox = useMemo(() => {
    const filtered = objectTypesDialogFilteredRows;
    if (filtered.length === 0) {
      return { checked: false, indeterminate: false };
    }
    const enabledCount = filtered.filter((r) => r.enabled).length;
    return {
      checked: enabledCount === filtered.length,
      indeterminate: enabledCount > 0 && enabledCount < filtered.length,
    };
  }, [objectTypesDialogFilteredRows]);

  const handleObjectTypesDialogSelectAll = (checked: boolean) => {
    const visible = new Set(
      objectTypesDialogFilteredRows.map((r) => r.name),
    );
    setObjectTypesDialogRows((prev) =>
      prev.map((r) =>
        visible.has(r.name) ? { ...r, enabled: checked } : r,
      ),
    );
  };

  const handleObjectTypeRowToggle = (name: string) => {
    setObjectTypesDialogRows((prev) =>
      prev.map((r) => (r.name === name ? { ...r, enabled: !r.enabled } : r)),
    );
  };

  const handleObjectTypeConfidenceChange = (name: string, value: number) => {
    setObjectTypesDialogRows((prev) =>
      prev.map((r) => (r.name === name ? { ...r, confidence: value } : r)),
    );
  };

  const handleObjectTypeConfidenceBlur = (name: string) => {
    setObjectTypesDialogRows((prev) =>
      prev.map((r) =>
        r.name === name
          ? { ...r, confidence: clampObjectTypeDialogConfidence(r.confidence) }
          : r,
      ),
    );
  };

  const handleObjectTypeThresholdChange = (name: string, value: number) => {
    const threshold = normalizeObjectTypeDialogThreshold(value);
    setObjectTypesDialogRows((prev) =>
      prev.map((r) => (r.name === name ? { ...r, threshold } : r)),
    );
  };

  const handleObjectTypeThresholdBlur = (name: string) => {
    setObjectTypesDialogRows((prev) =>
      prev.map((r) =>
        r.name === name
          ? {
              ...r,
              threshold: normalizeObjectTypeDialogThreshold(r.threshold),
            }
          : r,
      ),
    );
  };

  const handleAddNew = () => {
    if (readOnly) return;
    setSaveError(null);
    void onRequestZonesRefresh?.();
    setIsAddingNew(true);
    setNewModel({
      zoneName: zones.length > 0 ? zones[0].name : "",
      useCaseId: "",
      aiModel: "",
      confidenceScore: "",
      objectTypes: [],
      severityLabel: "",
    });
  };

  const handleCancelAdd = () => {
    setIsAddingNew(false);
    setSaveError(null);
    setObjectTypeConfigCount(null);
    setCreatedZoneProfileId(null);
    setNewModel({
      zoneName: "",
      useCaseId: "",
      aiModel: "",
      confidenceScore: "",
      objectTypes: [],
      severityLabel: "",
    });
  };

  /** After GET /cameras/{id}/zones returns, keep add-row Zone selection valid. */
  useEffect(() => {
    if (!isAddingNew || editingModel) return;
    setNewModel((prev) => {
      if (prev.zoneName && zones.some((z) => z.name === prev.zoneName)) {
        return prev;
      }
      const first = zones[0]?.name ?? "";
      return { ...prev, zoneName: first };
    });
  }, [zones, isAddingNew, editingModel]);

  const handleSaveNew = async (overrideObjectTypes?: string[]) => {
    if (!newModel.zoneName || !newModel.useCaseId) {
      setSaveError("Please select Zone and Use Case.");
      return;
    }

    const base = API_BASE.replace(/\/$/, "");
    if (!base || !cameraId) {
      setSaveError("Camera not selected or API base not configured.");
      return;
    }

    const selectedZone = zones.find((z) => z.name === newModel.zoneName);
    if (!selectedZone?.id) {
      setSaveError("Please select a valid zone.");
      return;
    }

    const useCaseId = newModel.useCaseId;
    const severity = mapSeverityToApi(newModel.severityLabel);
    const zonePid =
      createdZoneProfileId && isUuidString(createdZoneProfileId.trim())
        ? createdZoneProfileId.trim()
        : null;

    const body: PostCameraAiConfigBody = {
      camera_id: cameraId.trim(),
      use_case_id: useCaseId,
      zone_id: selectedZone.id,
      zone_profile_id: zonePid,
      severity,
      detection_cooldown_interval: COOLDOWN_INTERVAL_MIN,
      is_active: true,
    };

    const objectTypesToSave = overrideObjectTypes ?? newModel.objectTypes;

    setSaveError(null);
    setSaveInProgress(true);
    try {
      if (cameraSyncPayload != null) {
        await postCamerasCreate(base, cameraSyncPayload);
      }
      await postCameraAiConfigsCreate(base, body);
      const useCaseName =
        useCasesList.find((u) => u.id === newModel.useCaseId)?.name ??
        newModel.useCaseId;
      const model: ZoneAIModel = {
        id: `model-${Date.now()}`,
        zoneName: newModel.zoneName,
        aiModel: useCaseName,
        confidenceScore: newModel.confidenceScore
          ? parseInt(newModel.confidenceScore, 10)
          : 0,
        objectTypes: Array.isArray(objectTypesToSave) ? objectTypesToSave : [],
        createdAt: new Date(),
      };
      setModels((prev) => [...prev, model]);
      onAddModel?.(model);
      onConfigCreated?.();
      setCreatedZoneProfileId(null);
      handleCancelAdd();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to create config.";
      setSaveError(message);
      toast.error(message, TOAST_ERROR_POSITION);
    } finally {
      setSaveInProgress(false);
    }
  };

  const handleObjectTypesChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setNewModel({
      ...newModel,
      objectTypes: typeof value === "string" ? value.split(",") : value,
    });
  };

  /** Search then sort by Name (zoneName) — same toggle and compare pattern as ZonesTab. */
  const filteredModels = useMemo(() => {
    const query = searchTerm.toLowerCase();
    let list = models.filter((model) => {
      const matchesSearch =
        model.zoneName.toLowerCase().includes(query) ||
        model.aiModel.toLowerCase().includes(query);
      const matchesUseCase =
        !filterUseCaseName || model.aiModel === filterUseCaseName;
      return matchesSearch && matchesUseCase;
    });

    list.sort((a, b) => {
      const aValue = a.zoneName;
      const bValue = b.zoneName;
      if (aValue == null || bValue == null) return 0;
      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });

    return list;
  }, [models, searchTerm, filterUseCaseName, order, orderBy]);

  const paginatedModels = filteredModels.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  /** Use case names that actually appear in the AI model table (for filter dropdown) */
  const useCaseNamesInTable = useMemo(
    () =>
      [...new Set(models.map((m) => m.aiModel).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b),
      ),
    [models],
  );

  useEffect(() => {
    if (filterUseCaseName && !useCaseNamesInTable.includes(filterUseCaseName)) {
      setFilterUseCaseName("");
    }
  }, [filterUseCaseName, useCaseNamesInTable]);

  const showTableContent =
    !cameraAiConfigsLoading &&
    !cameraAiConfigsError &&
    cameraAiConfigs.length > 0;

  /** Icon: 3D box (empty state). */
  const EmptyStateIcon = () => (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      focusable="false"
      aria-hidden
      sx={{ width: 60, height: 60, color: "#9CA3AF" }}
    >
      <path
        fill="currentColor"
        d="M21.3353 12.0795L17.25 10.0357V5.25C17.2499 5.11075 17.2111 4.97428 17.1378 4.85586C17.0646 4.73743 16.9598 4.64174 16.8353 4.5795L12.3353 2.3295C12.2312 2.27748 12.1164 2.2504 12 2.2504C11.8836 2.2504 11.7689 2.27748 11.6648 2.3295L7.16475 4.5795C7.04019 4.64174 6.93542 4.73743 6.86216 4.85586C6.78891 4.97428 6.75007 5.11075 6.75 5.25V10.0365L2.66475 12.0787C2.54008 12.141 2.43523 12.2369 2.36197 12.3554C2.28871 12.474 2.24994 12.6106 2.25 12.75V18C2.24994 18.1394 2.28871 18.276 2.36197 18.3946C2.43523 18.5131 2.54008 18.609 2.66475 18.6713L7.16475 20.9213C7.26885 20.9733 7.38363 21.0003 7.5 21.0003C7.61637 21.0003 7.73115 20.9733 7.83525 20.9213L12 18.8385L16.1647 20.9213C16.2688 20.9733 16.3836 21.0003 16.5 21.0003C16.6164 21.0003 16.7312 20.9733 16.8353 20.9213L21.3353 18.6713C21.4599 18.609 21.5648 18.5131 21.638 18.3946C21.7113 18.276 21.7501 18.1394 21.75 18V12.75C21.7501 12.6106 21.7113 12.474 21.638 12.3554C21.5648 12.2369 21.4599 12.141 21.3353 12.0787M15.75 10.0357L12.75 11.5357V7.96275L15.75 6.46275V10.0357ZM12 3.83775L14.823 5.25L12 6.6615L9.177 5.25L12 3.83775ZM8.25 6.46275L11.25 7.96275V11.5357L8.25 10.0357V6.46275ZM6.75 19.0365L3.75 17.5365V13.9635L6.75 15.4635V19.0365ZM7.5 14.1615L4.677 12.75L7.5 11.3385L10.323 12.75L7.5 14.1615ZM8.25 15.4635L11.25 13.9635V17.5365L8.25 19.0365V15.4635ZM15.75 19.0365L12.75 17.5365V13.9635L15.75 15.4635V19.0365ZM16.5 14.1615L13.677 12.75L16.5 11.3385L19.323 12.75L16.5 14.1615ZM20.25 17.5365L17.25 19.0365V15.4635L20.25 13.9635V17.5365Z"
      />
    </Box>
  );

  const getCenterMessage = () => {
    if (cameraAiConfigsLoading) {
      return (
        <Typography
          variant="body2"
          sx={{ color: "#4A5565", fontFamily: "Roboto, sans-serif" }}
        >
          Loading camera AI configs...
        </Typography>
      );
    }
    if (cameraAiConfigsError) {
      return (
        <Typography
          variant="body2"
          sx={{ color: "#D32F2F", fontFamily: "Roboto, sans-serif" }}
        >
          {cameraAiConfigsError}
        </Typography>
      );
    }
    if (cameraAiConfigs.length === 0) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <EmptyStateIcon />
          <Typography
            variant="body2"
            sx={{ color: "#4A5565", fontFamily: "Roboto, sans-serif" }}
          >
            No items available.
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const emptyStateContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1.5,
      }}
    >
      <EmptyStateIcon />
      <Typography
        variant="body2"
        sx={{ color: "#4A5565", fontFamily: "Roboto, sans-serif" }}
      >
        No items available.
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "#fff",
      }}
    >
      {/* Section title: Zone AI Models (larger, dark blue per screenshot) */}
      <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
        <Typography
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: 20,
            fontWeight: 700,
            color: "#0D47A1",
          }}
        >
          Zone AI Models
        </Typography>
      </Box>

      {/* Search and action bar: search left, Zone AI Model button right */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <TextField
          size="small"
          placeholder="Search zone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9CA3AF", fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: 240,
            "& .MuiOutlinedInput-root": {
              bgcolor: "#fff",
              fontFamily: "Roboto, sans-serif",
              fontSize: 14,
              height: 40,
              borderRadius: "8px",
              "& fieldset": {
                borderColor: "#E5E7EB",
              },
            },
          }}
        />

        <Tooltip
          title={
            readOnly
              ? "View only: open this camera with Edit Zone to add or change AI models."
              : ""
          }
          arrow
          disableHoverListener={!readOnly}
        >
          <span>
            <Button
              variant="contained"
              disableElevation
              onClick={handleAddNew}
              disabled={readOnly}
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                boxSizing: "border-box",
                minWidth: 125,
                height: 31,
                borderRadius: "10px",
                border: "1px solid #D0D0D0",
                padding: "4px 16px",
                gap: "8px",
                bgcolor: " #2932E5",

                color: "#FFFFFF",
                textTransform: "none",
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                fontWeight: 500,
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "none",
                  border: "1px solid #B0B0B0",
                },
                "&:disabled": {
                  bgcolor: "#EEEEEE",
                  color: "#9E9E9E",
                  border: "1px solid #E5E7EB",
                },
              }}
            >
              Zone AI Model
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Table + add/edit rows + pagination: flex column so pagination stays at bottom */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <TableContainer
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            width: "100%",
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              width: "100%",
              tableLayout: "fixed",
              borderCollapse: "collapse",
              height:
                (!showTableContent && !isAddingNew) ||
                (showTableContent && paginatedModels.length === 0)
                  ? "100%"
                  : undefined,
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    bgcolor: "#fff",
                    borderBottom: "1px solid #E5E7EB",
                    px: 2,
                    py: 1.25,
                    width: 110,
                    minWidth: 100,
                    cursor: "pointer",
                  }}
                  onClick={() => handleSort("name")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography
                      component="span"
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 500,
                        fontSize: 14,
                        color: "#1976D2",
                        letterSpacing: "0.1px",
                      }}
                    >
                      Name
                    </Typography>
                    {nameSortEngaged &&
                      orderBy === "name" &&
                      (order === "asc" ? (
                        <ArrowUpwardIcon
                          fontSize="small"
                          sx={{ color: "#1976D2", fontSize: 18 }}
                        />
                      ) : (
                        <ArrowDownwardIcon
                          fontSize="small"
                          sx={{ color: "#1976D2", fontSize: 18 }}
                        />
                      ))}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    bgcolor: "#fff",
                    borderBottom: "1px solid #E5E7EB",
                    px: 2,
                    py: 1.25,
                    width: 130,
                    minWidth: 120,
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: "Roboto, sans-serif",
                      fontWeight: 500,
                      fontSize: 14,
                      color: "#1976D2",
                      letterSpacing: "0.1px",
                    }}
                  >
                    Use case
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    bgcolor: "#fff",
                    borderBottom: "1px solid #E5E7EB",
                    px: 2,
                    py: 1.25,
                    width: 130,
                    minWidth: 120,
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: "Roboto, sans-serif",
                      fontWeight: 500,
                      fontSize: 14,
                      color: "#1976D2",
                      letterSpacing: "0.1px",
                    }}
                  >
                    Severity
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    bgcolor: "#fff",
                    borderBottom: "1px solid #E5E7EB",
                    px: 2,
                    py: 1.25,
                    width: 140,
                    minWidth: 100,
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: "Roboto, sans-serif",
                      fontWeight: 500,
                      fontSize: 14,
                      color: "#1976D2",
                      letterSpacing: "0.1px",
                    }}
                  >
                    Object Type
                  </Typography>
                </TableCell>
                {!readOnly && (
                  <TableCell
                    align="center"
                    sx={{
                      bgcolor: "#fff",
                      borderBottom: "1px solid #E5E7EB",
                      px: 1,
                      py: 1.25,
                      width: 132,
                      minWidth: 112,
                      position: "sticky",
                      right: 0,
                      zIndex: 4,
                      boxShadow: "-6px 0 12px -8px rgba(0,0,0,0.12)",
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 500,
                        fontSize: 14,
                        color: "#1976D2",
                        letterSpacing: "0.1px",
                      }}
                    >
                      Actions
                    </Typography>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {!showTableContent && !isAddingNew ? (
                <TableRow>
                  <TableCell
                    colSpan={readOnly ? 4 : 5}
                    align="center"
                    sx={{
                      py: 0,
                      verticalAlign: "middle",
                      borderBottom: "none",
                      minHeight: "min(400px, calc(100vh - 320px))",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                      }}
                    >
                      {getCenterMessage()}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : showTableContent && paginatedModels.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={readOnly ? 4 : 5}
                    align="center"
                    sx={{
                      py: 0,
                      verticalAlign: "middle",
                      borderBottom: "none",
                      minHeight: "min(400px, calc(100vh - 320px))",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                      }}
                    >
                      {emptyStateContent}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {showTableContent &&
                    paginatedModels.map((model) => (
                      <TableRow
                        key={model.id}
                        onContextMenu={(e) => {
                          if (!readOnly) handleRowContextMenu(e, model);
                        }}
                        sx={{
                          height: 48,
                          "& > .MuiTableCell-root": {
                            borderBottom: "1px solid #E5E7EB",
                            verticalAlign: "middle",
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            px: 2,
                            py: 1,
                            fontFamily: "Roboto, sans-serif",
                            fontSize: 14,
                            color: "#212121",
                          }}
                        >
                          {model.zoneName}
                        </TableCell>
                        <TableCell
                          sx={{
                            px: 2,
                            py: 1,
                          }}
                        >
                          <Chip
                            label={model.aiModel}
                            size="small"
                            sx={{
                              bgcolor: "#E3F2FD",
                              color: "#1565C0",
                              fontFamily: "Roboto, sans-serif",
                              fontSize: 13,
                              fontWeight: 500,
                              height: 28,
                              borderRadius: "50px",
                              "& .MuiChip-label": {
                                px: 1.5,
                                py: 0.5,
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            px: 2,
                            py: 1,
                          }}
                        >
                          <Chip
                            label={getSeverityLabel(model.confidenceScore)}
                            size="small"
                            sx={{
                              ...getSeverityChipStyle(
                                getSeverityLabel(model.confidenceScore),
                              ),
                              fontFamily: "Roboto, sans-serif",
                              fontSize: 12,
                              fontWeight: 500,
                              height: 26,
                              borderRadius: "50px",
                              "& .MuiChip-label": { px: 1.25, py: 0.5 },
                            }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            px: 2,
                            py: 1,
                            minWidth: 0,
                          }}
                        >
                          {readOnly ? (
                            <Typography
                              sx={{
                                fontFamily: "Roboto, sans-serif",
                                fontSize: 14,
                                color: "text.secondary",
                              }}
                            >
                              {model.profileCount ?? model.objectTypes.length}
                            </Typography>
                          ) : (
                            <Box
                              component="div"
                              onClick={() =>
                                void handleOpenObjectTypesDialog(model)
                              }
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  void handleOpenObjectTypesDialog(model);
                                }
                              }}
                              sx={{
                                display: "block",
                                width: "100%",
                                fontFamily: "Roboto, sans-serif",
                                fontSize: 14,
                                color: "#1976D2",
                                textDecoration: "underline",
                                cursor: "pointer",
                              }}
                            >
                              {model.profileCount ?? model.objectTypes.length}
                            </Box>
                          )}
                        </TableCell>
                        {!readOnly && (
                          <TableCell
                            align="center"
                            sx={{
                              px: 0.5,
                              py: 1,
                              bgcolor: "#fff",
                              position: "sticky",
                              right: 0,
                              zIndex: 1,
                              boxShadow: "-6px 0 12px -8px rgba(0,0,0,0.08)",
                            }}
                          >
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.25,
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Tooltip
                                placement="top"
                                title={
                                  getToggleDisabledReason(model) ??
                                  "Turn detection on or off for this row"
                                }
                              >
                                <span>
                                  <Switch
                                    checked={model.isActive ?? true}
                                    disabled={
                                      Boolean(getToggleDisabledReason(model)) ||
                                      activeToggleSavingId === model.id
                                    }
                                    onChange={(e) =>
                                      void handleToggleModelActive(
                                        model,
                                        e.target.checked,
                                      )
                                    }
                                    size="small"
                                    sx={{
                                      "& .MuiSwitch-switchBase.Mui-checked": {
                                        color: "#2932E5",
                                      },
                                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                        {
                                          backgroundColor: "#2932E5",
                                        },
                                    }}
                                    inputProps={{
                                      "aria-label": `Toggle active for ${model.zoneName} ${model.aiModel}`,
                                    }}
                                  />
                                </span>
                              </Tooltip>
                              <IconButton
                                size="small"
                                onClick={(e) => handleMenuOpen(e, model)}
                                sx={{ p: 0.5 }}
                                aria-label="Actions"
                              >
                                <MoreVertIcon
                                  sx={{ fontSize: 20, color: "#4A5565" }}
                                />
                              </IconButton>
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Add row below table (Zone, Use Case, Severity, Object Type, X, Save) - visible when "Zone AI Model" clicked */}
        {isAddingNew && !editingModel && !readOnly && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              px: 2,
              py: 1,
              borderTop: "1px solid #E5E7EB",
              borderBottom: "1px solid #E5E7EB",
              bgcolor: "#FAFAFA",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                alignItems: "center",
                columnGap: 1,
                rowGap: 1,
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                overflow: "hidden",
                boxSizing: "border-box",
              }}
            >
            <FormControl
              size="small"
              sx={{ flex: "1 1 0%", minWidth: 72, maxWidth: 200 }}
            >
              <Select
                fullWidth
                value={newModel.zoneName}
                onChange={(e) =>
                  setNewModel({ ...newModel, zoneName: e.target.value })
                }
                displayEmpty
                sx={{
                  height: 40,
                  bgcolor: "#fff",
                  borderRadius: "8px",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: 14,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E0E0E0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#BDBDBD",
                  },
                }}
              >
                <MenuItem
                  value=""
                  disabled
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: 14,
                    color: "#9E9E9E",
                  }}
                >
                  Zone
                </MenuItem>
                {zones.map((zone) => (
                  <MenuItem
                    key={zone.id}
                    value={zone.name}
                    sx={{ fontFamily: "Roboto, sans-serif", fontSize: 14 }}
                  >
                    {zone.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{ minWidth: 160, width: 160, flexShrink: 0 }}
            >
              <Select
                value={newModel.useCaseId}
                onChange={(e) =>
                  setNewModel({ ...newModel, useCaseId: e.target.value })
                }
                displayEmpty
                disabled={useCasesLoading}
                sx={{
                  height: 40,
                  bgcolor: "#fff",
                  borderRadius: "8px",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: 14,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E0E0E0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#BDBDBD",
                  },
                }}
              >
                <MenuItem
                  value=""
                  disabled
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: 14,
                    color: "#9E9E9E",
                  }}
                >
                  {useCasesLoading ? "Loading use cases..." : "Use Case"}
                </MenuItem>
                {useCasesList.map((uc) => (
                  <MenuItem
                    key={uc.id}
                    value={uc.id}
                    sx={{ fontFamily: "Roboto, sans-serif", fontSize: 14 }}
                  >
                    {uc.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{ minWidth: 160, width: 160, flexShrink: 0 }}
            >
              <Select
                value={newModel.severityLabel ?? ""}
                onChange={(e) => {
                  const label = e.target.value as string;
                  const score =
                    label === "8-10 Critical"
                      ? 95
                      : label === "5-8 High"
                        ? 65
                        : label === "3-5 Medium"
                          ? 40
                          : 20;
                  setNewModel((prev) => ({
                    ...prev,
                    severityLabel: label,
                    confidenceScore: String(score),
                  }));
                }}
                displayEmpty
                sx={{
                  height: 40,
                  bgcolor: "#fff",
                  borderRadius: "8px",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: 14,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E0E0E0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#BDBDBD",
                  },
                }}
              >
                <MenuItem
                  value=""
                  disabled
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: 14,
                    color: "#9E9E9E",
                  }}
                >
                  Severity
                </MenuItem>
                {SEVERITY_OPTIONS.map((opt) => (
                  <MenuItem
                    key={opt}
                    value={opt}
                    sx={{ fontFamily: "Roboto, sans-serif", fontSize: 14 }}
                  >
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              onClick={handleOpenObjectTypesDialogForNewModel}
              disabled={saveInProgress}
              sx={{
                flexShrink: 0,
                minWidth: 160,
                height: 40,
                bgcolor: "#fff",
                borderRadius: "8px",
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                color: objectTypeConfigCount != null ? "#212121" : "#9E9E9E",
                borderColor: "#E0E0E0",
                textTransform: "none",
                "&:hover": {
                  bgcolor: "#FAFAFA",
                  borderColor: "#BDBDBD",
                },
                "&:disabled": {
                  bgcolor: "#F5F5F5",
                  color: "#9E9E9E",
                },
              }}
            >
              Object Type
              {objectTypeConfigCount != null
                ? ` (${objectTypeConfigCount})`
                : ""}
            </Button>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexShrink: 0,
              }}
            >
              <IconButton
                size="small"
                onClick={handleCancelAdd}
                disabled={saveInProgress}
                aria-label="Remove"
                sx={{
                  bgcolor: "#D32F2F",
                  color: "white",
                  width: 40,
                  height: 40,
                  borderRadius: "8px",
                  "&:hover": { bgcolor: "#B71C1C" },
                  "&:disabled": { bgcolor: "#EF9A9A", color: "white" },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleSaveNew()}
                disabled={saveInProgress}
                aria-label="Save"
                sx={{
                  bgcolor: "#2932E5",
                  color: "white",
                  width: 40,
                  height: 40,
                  borderRadius: "8px",
                  "&:hover": { bgcolor: "#1F25B0" },
                  "&:active": { bgcolor: "#fff", color: "#2932E5" },
                  "&:disabled": { bgcolor: "#9FA8DA", color: "white" },
                }}
              >
                {saveInProgress ? (
                  <CircularProgress size={20} sx={{ color: "white" }} />
                ) : (
                  <SaveIcon sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            </Box>
            </Box>
            {saveError && (
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  color: "#D32F2F",
                  fontFamily: "Roboto, sans-serif",
                  width: "100%",
                }}
              >
                {saveError}
              </Typography>
            )}
          </Box>
        )}

        {/* Inline edit row – same layout as add row; Save calls PUT /camera-ai-configs/{config_id} */}
        {editingModel && !readOnly && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              px: 2,
              py: 1.5,
              borderTop: "1px solid #E5E7EB",
              borderBottom: "1px solid #E5E7EB",
              bgcolor: "#FAFAFA",
            }}
          >
            <Box
              sx={{
                overflowX: "auto",
                overflowY: "hidden",
                width: "100%",
                maxWidth: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "nowrap",
                  alignItems: "center",
                  gap: "8px",
                  width: "max-content",
                  minWidth: "100%",
                  boxSizing: "border-box",
                  
                }}
              >
            <FormControl
              size="small"
              sx={{ minWidth: 140, width: 140, flexShrink: 0 }}
            >
              <Select
                value={editForm.zoneId}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, zoneId: e.target.value }))
                }
                displayEmpty
                sx={{
                  height: 40,
                  bgcolor: "#fff",
                  borderRadius: "8px",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: 14,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E0E0E0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#BDBDBD",
                  },
                }}
              >
                <MenuItem
                  value=""
                  disabled
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: 14,
                    width: "50px",
                    color: "#9E9E9E",
                  }}
                >
                  Zone
                </MenuItem>
                {zones.map((z) => (
                  <MenuItem
                    key={z.id}
                    value={z.id}
                    sx={{ fontFamily: "Roboto, sans-serif", fontSize: 14 }}
                  >
                    {z.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{ minWidth: 160, width: 160, flexShrink: 0 }}
            >
              <Select
                value={editForm.useCaseId}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, useCaseId: e.target.value }))
                }
                displayEmpty
                disabled={useCasesLoading}
                sx={{
                  height: 40,
                  bgcolor: "#fff",
                  borderRadius: "8px",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: 14,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E0E0E0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#BDBDBD",
                  },
                }}
              >
                <MenuItem
                  value=""
                  disabled
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: 14,
                    color: "#9E9E9E",
                  }}
                >
                  {useCasesLoading ? "Loading use cases..." : "Use Case"}
                </MenuItem>
                {useCasesList.map((uc) => (
                  <MenuItem
                    key={uc.id}
                    value={uc.id}
                    sx={{ fontFamily: "Roboto, sans-serif", fontSize: 14 }}
                  >
                    {uc.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{ minWidth: 160, width: 160, flexShrink: 0 }}
            >
              <Select
                value={editForm.severityLabel}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, severityLabel: e.target.value }))
                }
                displayEmpty
                sx={{
                  height: 40,
                  bgcolor: "#fff",
                  borderRadius: "8px",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: 14,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E0E0E0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#BDBDBD",
                  },
                }}
              >
                <MenuItem
                  value=""
                  disabled
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: 14,
                    color: "#9E9E9E",
                  }}
                >
                  Severity
                </MenuItem>
                {SEVERITY_OPTIONS.map((opt) => (
                  <MenuItem
                    key={opt}
                    value={opt}
                    sx={{ fontFamily: "Roboto, sans-serif", fontSize: 14 }}
                  >
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              disabled={!editForm.zoneId}
              onClick={() => {
                if (!editingModel) return;
                const zoneId = editForm.zoneId || editingModel.zoneId || "";
                void handleOpenObjectTypesDialog({ ...editingModel, zoneId });
              }}
              sx={{
                minWidth: 160,
                height: 40,
                bgcolor: "#fff",
                borderRadius: "8px",
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                color: "#212121",
                borderColor: "#E0E0E0",
                textTransform: "none",
              }}
            >
              Object Type
              {editingModel
                ? ` (${editingModel.profileCount ?? editingModel.objectTypes.length})`
                : ""}
            </Button>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexShrink: 0,
              }}
            >
              <IconButton
                size="small"
                onClick={handleEditCancel}
                disabled={editSaving}
                aria-label="Cancel"
                sx={{
                  bgcolor: "#D32F2F",
                  color: "white",
                  width: 40,
                  height: 40,
                  borderRadius: "8px",
                  "&:hover": { bgcolor: "#B71C1C" },
                  "&:disabled": { bgcolor: "#EF9A9A", color: "white" },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleEditSave}
                disabled={editSaving || !editForm.zoneId || !editForm.useCaseId}
                aria-label="Save"
                sx={{
                  bgcolor: "#2932E5",
                  color: "white",
                  width: 40,
                  height: 40,
                  borderRadius: "8px",
                  "&:hover": { bgcolor: "#1F25B0" },
                  "&:disabled": { bgcolor: "#9FA8DA", color: "white" },
                }}
              >
                {editSaving ? (
                  <CircularProgress size={20} sx={{ color: "white" }} />
                ) : (
                  <SaveIcon sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            </Box>
              </Box>
            </Box>
            {editSaveError && (
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  color: "#D32F2F",
                  fontFamily: "Roboto, sans-serif",
                  width: "100%",
                }}
              >
                {editSaveError}
              </Typography>
            )}
          </Box>
        )}

        {/* Pagination bar: Total Items, Rows per page, range, prev/next only */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "10px",
            borderTop: "1px solid #E5E7EB",
            bgcolor: "#F2F2F2",
            minHeight: 52,
            px: 2,
            borderRadius: "0 0 8px 8px",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: 14,
              color: "#374151",
            }}
          >
            Total Items: {filteredModels.length}
          </Typography>
          <TablePagination
            component="div"
            count={filteredModels.length}
            page={page}
            onPageChange={(_event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 14, 25, 50]}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} of ${count}`
            }
            labelRowsPerPage="Rows per page:"
            sx={{
              border: "none",
              "& .MuiTablePagination-toolbar": { minHeight: 52, px: 0 },
              "& .MuiTablePagination-selectLabel": {
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                color: "#374151",
              },
              "& .MuiTablePagination-displayedRows": {
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                color: "#374151",
              },
              "& .MuiTablePagination-select": {
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                color: "#374151",
              },
              "& .MuiTablePagination-actions": {
                color: "#374151",
              },
            }}
          />
        </Box>
      </Box>

      {/* Cooldown interval & Object Types — opens from Object Type column */}
      <Dialog
        open={objectTypesDialogOpen}
        onClose={handleCloseObjectTypesDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "10px",
            boxShadow: "0px 25px 50px rgba(0,0,0,0.25)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: 18,
            fontWeight: 600,
            color: "#101828",
            borderBottom: "1px solid #E5E7EB",
            pb: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          Cooldown Interval & Object Types
          <IconButton
            size="small"
            onClick={handleCloseObjectTypesDialog}
            sx={{ p: 0 }}
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 20, color: "#99A1AF" }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2.5, pt: 2, overflow: "hidden" }}>
          {objectTypesDialogError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setObjectTypesDialogError(null)}
            >
              {objectTypesDialogError}
            </Alert>
          )}
          <Box sx={{ mb: 2.5 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#374151",
                }}
              >
                Cooldown Interval
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#1976D2",
                }}
              >
                {COOLDOWN_INTERVAL_MIN} - {COOLDOWN_INTERVAL_MAX} seconds
              </Typography>
            </Box>
            <TextField
              type="number"
              fullWidth
              size="small"
              disabled={objectTypesDialogLoading}
              value={objectTypesDialogCooldownSeconds}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "") {
                  setObjectTypesDialogCooldownSeconds(COOLDOWN_INTERVAL_MIN);
                  return;
                }
                const n = parseInt(v, 10);
                if (!Number.isNaN(n)) {
                  setObjectTypesDialogCooldownSeconds(n);
                }
              }}
              onBlur={() =>
                setObjectTypesDialogCooldownSeconds((prev) =>
                  clampCooldownIntervalSeconds(prev),
                )
              }
              inputProps={{
                min: COOLDOWN_INTERVAL_MIN,
                max: COOLDOWN_INTERVAL_MAX,
                step: 1,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#fff",
                  borderRadius: "8px",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: 14,
                },
              }}
            />
          </Box>

          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: 14,
              fontWeight: 600,
              color: "#101828",
              mb: 1.5,
            }}
          >
            Object types & Confidence
          </Typography>

          <TextField
            fullWidth
            size="small"
            placeholder="Search"
            value={objectTypesDialogSearch}
            onChange={(e) => setObjectTypesDialogSearch(e.target.value)}
            disabled={objectTypesDialogLoading}
            sx={{
              mb: 1.5,
              "& .MuiOutlinedInput-root": {
                bgcolor: "#fff",
                borderRadius: "8px",
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#9E9E9E", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          {objectTypesDialogLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 6,
              }}
            >
              <CircularProgress
                size={40}
                sx={{ color: "#2932E5" }}
                aria-label="Loading zone profiles"
              />
            </Box>
          ) : (
            <TableContainer
              sx={{
                maxHeight: 280,
                overflow: "auto",
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                bgcolor: "#fff",
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      padding="checkbox"
                      sx={{
                        bgcolor: "#FAFAFA",
                        borderBottom: "1px solid #E5E7EB",
                        width: 44,
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={objectTypesDialogSelectAllCheckbox.checked}
                        indeterminate={
                          objectTypesDialogSelectAllCheckbox.indeterminate
                        }
                        onChange={(e) =>
                          handleObjectTypesDialogSelectAll(e.target.checked)
                        }
                        disabled={
                          objectTypesDialogLoading ||
                          objectTypesDialogFilteredRows.length === 0
                        }
                        inputProps={{
                          "aria-label": "Select all object types in list",
                        }}
                        sx={{
                          p: 0,
                          "&.Mui-checked": { color: "#2932E5" },
                          "&.MuiCheckbox-indeterminate": {
                            color: "#2932E5",
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 600,
                        fontSize: 14,
                        color: "#374151",
                        borderBottom: "1px solid #E5E7EB",
                        bgcolor: "#FAFAFA",
                      }}
                    >
                      Object Type
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 600,
                        fontSize: 14,
                        color: "#374151",
                        borderBottom: "1px solid #E5E7EB",
                        width: 140,
                        bgcolor: "#FAFAFA",
                      }}
                      title={`Confidence (${OBJECT_TYPE_DIALOG_CONFIDENCE_MIN}–${OBJECT_TYPE_DIALOG_CONFIDENCE_MAX})`}
                    >
                      Confidence
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 600,
                        fontSize: 14,
                        color: "#374151",
                        borderBottom: "1px solid #E5E7EB",
                        width: 120,
                        bgcolor: "#FAFAFA",
                      }}
                      title={`Integer ≥ ${OBJECT_TYPE_DIALOG_THRESHOLD_MIN}`}
                    >
                      Threshold
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {objectTypesDialogFilteredRows.map((row) => (
                    <TableRow
                      key={row.name}
                      sx={{ "&:last-child td": { borderBottom: 0 } }}
                    >
                      <TableCell
                        padding="checkbox"
                        sx={{
                          borderBottom: "1px solid #E5E7EB",
                          py: 1,
                        }}
                      >
                        <Checkbox
                          checked={row.enabled}
                          onChange={() => handleObjectTypeRowToggle(row.name)}
                          size="small"
                          sx={{
                            p: 0,
                            "&.Mui-checked": { color: "#2932E5" },
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          borderBottom: "1px solid #E5E7EB",
                          py: 1,
                          fontFamily: "Roboto, sans-serif",
                          fontSize: 14,
                          color: "#374151",
                        }}
                      >
                        {row.name}
                      </TableCell>
                      <TableCell
                        sx={{ borderBottom: "1px solid #E5E7EB", py: 1 }}
                      >
                        <TextField
                          type="number"
                          size="small"
                          value={row.confidence}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === "") return;
                            const num = Number(raw);
                            if (!Number.isFinite(num)) return;
                            handleObjectTypeConfidenceChange(row.name, num);
                          }}
                          onBlur={() =>
                            handleObjectTypeConfidenceBlur(row.name)
                          }
                          inputProps={{
                            min: OBJECT_TYPE_DIALOG_CONFIDENCE_MIN,
                            max: OBJECT_TYPE_DIALOG_CONFIDENCE_MAX,
                            step: 0.01,
                          }}
                          title={`${OBJECT_TYPE_DIALOG_CONFIDENCE_MIN}–${OBJECT_TYPE_DIALOG_CONFIDENCE_MAX}`}
                          sx={{
                            width: 100,
                            "& .MuiOutlinedInput-root": {
                              height: 36,
                              fontSize: 14,
                              fontFamily: "Roboto, sans-serif",
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{ borderBottom: "1px solid #E5E7EB", py: 1 }}
                      >
                        <TextField
                          type="number"
                          size="small"
                          value={row.threshold}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === "") return;
                            const num = Number(raw);
                            if (!Number.isFinite(num)) return;
                            handleObjectTypeThresholdChange(row.name, num);
                          }}
                          onBlur={() =>
                            handleObjectTypeThresholdBlur(row.name)
                          }
                          inputProps={{
                            min: OBJECT_TYPE_DIALOG_THRESHOLD_MIN,
                            step: 1,
                          }}
                          title={`Integer ≥ ${OBJECT_TYPE_DIALOG_THRESHOLD_MIN}`}
                          sx={{
                            width: 80,
                            "& .MuiOutlinedInput-root": {
                              height: 36,
                              fontSize: 14,
                              fontFamily: "Roboto, sans-serif",
                            },
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions
          sx={{ px: 2, py: 1.5, borderTop: "1px solid #E5E7EB", gap: 1 }}
        >
          <Button
            onClick={handleCloseObjectTypesDialog}
            disabled={objectTypesDialogLoading || objectTypesDialogOkLoading}
            sx={{
              textTransform: "none",
              fontFamily: "Roboto, sans-serif",
              fontSize: 14,
              color: "#4A5565",
              border: "1px solid #E5E7EB",
              "&:hover": { bgcolor: "#F5F5F5", borderColor: "#E5E7EB" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmObjectTypesDialog}
            disabled={objectTypesDialogLoading || objectTypesDialogOkLoading}
            sx={{
              textTransform: "none",
              fontFamily: "Roboto, sans-serif",
              fontSize: 14,
              bgcolor: "#2932E5",
              "&:hover": { bgcolor: "#1f28b8" },
              minWidth: 88,
            }}
          >
            {objectTypesDialogOkLoading ? (
              <CircularProgress
                size={22}
                sx={{ color: "#fff" }}
                aria-label="Saving"
              />
            ) : (
              "Ok"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions menu: 3-dot or right-click on row — Edit / Delete */}
      <Menu
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          anchorEl
            ? { top: anchorEl.mouseY + 2, left: anchorEl.mouseX + 2 }
            : undefined
        }
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            boxShadow: "0 2px 12px rgba(0, 0, 0, 0.12)",
            borderRadius: "8px",
            bgcolor: "#FAFAFA",
          },
        }}
      >
        <MenuItem
          onClick={handleEdit}
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: 14,
            color: "#212121",
            px: 2,
            py: 1.25,
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={handleDelete}
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: 14,
            color: "#D32F2F",
            px: 2,
            py: 1.25,
          }}
        >
          Delete
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog — DELETE /camera-ai-configs/ body `{ ids: [config_row_id] }` */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (!deleteSaving) {
            setDeleteDialogOpen(false);
            setDeleteError(null);
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: "8px",
            minWidth: 400,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: "18px",
            fontWeight: 500,
            color: "#212121",
          }}
        >
          Delete AI model
        </DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setDeleteError(null)}
            >
              {deleteError}
            </Alert>
          )}
          <Box
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              color: "#4A5565",
              lineHeight: 1.5,
            }}
          >
            This AI model row will be soft-deleted (moved to the recycle bin)
            and kept for 60 days. You can restore or permanently delete it from
            the Recycle Bin tab.
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              if (!deleteSaving) {
                setDeleteDialogOpen(false);
                setDeleteError(null);
              }
            }}
            disabled={deleteSaving}
            sx={{
              textTransform: "none",
              fontFamily: "Arial, sans-serif",
              fontSize: "14px",
              color: "#4A5565",
              px: 2,
              py: 0.5,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void confirmDelete()}
            disabled={deleteSaving}
            sx={{
              bgcolor: "#2932E5",
              color: "white",
              textTransform: "none",
              fontFamily: "Arial, sans-serif",
              fontSize: "14px",
              px: 2,
              py: 0.5,
              borderRadius: "10px",
              border: "1px solid #2932E5",
              "&:hover": {
                bgcolor: "#1F25B0",
              },
              "&:disabled": { bgcolor: "#9FA8DA", color: "white" },
            }}
          >
            {deleteSaving ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
